import axios from "axios";
import store from "../store";

export class GameGroupGame {
    constructor({
        group_id,
        group_name,
        brand_id,
        brand_name,
        code,
        game_id,
        game_name,
        version_id,
        provider_game_id,
        version_created_at,
        display_order
    }) {
        this.group_id = group_id;
        this.group_name = group_name;
        this.brand_id = brand_id;
        this.brand_name = brand_name;
        this.code = code;
        this.game_id = game_id;
        this.game_name = game_name;
        this.version_id = version_id;
        this.provider_game_id = provider_game_id;
        this.version_created_at = version_created_at;
        this.display_order = display_order || 0;
    }
}

export class GameGroup {
    constructor({
        group_id,
        group_name,
        brand_id,
        created_at,
        brand_name,
        code,
        cognito_pool_id,
        cognito_client_id,
        theme_name,
        pinpoint_pool_arn,
        updated_at,
        game_group_games = []
    }) {
        this.group_id = group_id;
        this.group_name = group_name;
        this.brand_id = brand_id;
        this.created_at = created_at;
        this.brand_name = brand_name;
        this.code = code;
        this.cognito_pool_id = cognito_pool_id;
        this.cognito_client_id = cognito_client_id;
        this.theme_name = theme_name;
        this.pinpoint_pool_arn = pinpoint_pool_arn;
        this.updated_at = updated_at;
        // Ensure game_group_games is always an array
        const safeGames = Array.isArray(game_group_games) ? game_group_games : [];
        this.game_group_games = safeGames.map(g => new GameGroupGame(g));
    }
}

class GameGroupListItem {
    constructor(group_id, brand_id, game_count, group_name, brand_name, catalog_names) {
        this.group_id = group_id;
        this.brand_id = brand_id;
        this.game_count = game_count;
        this.group_name = group_name;
        this.brand_name = brand_name;
        this.catalog_names = catalog_names;
    }
}

class GameCatalogListItem {
    constructor(
        catalog_id,
        catalog_name,
        brand_id,
        brand_name,
        is_default,
        group_count,
        start_time,
        end_time
    ) {
        this.catalog_id = catalog_id;
        this.catalog_name = catalog_name;
        this.brand_id = brand_id;
        this.brand_name = brand_name;
        this.is_default = is_default;
        this.group_count = group_count || 0;
        this.start_time = start_time;
        this.end_time = end_time;
    }
}

export class BrandUserOverview {
    constructor(data = {}) {
        // Core Identity
        this.authentication_provider = data.authentication_provider || "";
        this.registration_date = data.registration_date || null;
        this.global_user_id = data.global_user_id || 0;
        this.brand_user_id = data.brand_user_id || 0;
        this.brand_code = data.brand_code || "giddyup";
        this.brand_name = data.brand_name || "";
        this.external_id = data.external_id || "";
        this.display_name = data.display_name || "";
        this.email = data.email || "";
        this.avatar_url = data.avatar_url || "";
        this.last_login_date = data.last_login_date || null;

        // PII fields
        this.name = data.name || null;
        this.address = data.address || null;
        this.dob = data.dob || null;
        this.phone = data.phone || null;
        this.is_depositor = data.is_depositor || false;

        // Session State
        this.is_currently_logged_in = data.is_currently_logged_in || false;

        // Verification Status (simplified)
        this.verification_status = data.verification_status || "";
        this.kyc_status = data.kyc_status || "";
        this.doc_upload_status = data.doc_upload_status || "";
        // Completed verifications, one row per type (#2586).
        this.verifications = data.verifications || [];

        // Grouped Wallets
        this.wallets = data.wallets || {
            gold_coin_balance: 0,
            sweeps_coin_balance: 0,
            credits: {
                playable: 0,
                total: 0,
                ringfenced: 0,
                unconverted: 0,
                converted: 0
            },
            usd: {
                playable: 0,
                total: 0,
                ringfenced: 0,
                unconverted: 0,
                converted: 0
            }
        };

        // Grouped Stats
        this.stats = data.stats || {
            total_purchases: 0,
            total_value_purchased: 0,
            total_sweeps_coins_granted: 0,
            total_deposits: 0,
            total_deposit_value: 0,
            total_redemptions: 0,
            total_redemption_value: 0,
            pending_redemption_value: 0,
            has_pending_redemption: false,
            total_withdrawals: 0,
            total_withdrawal_value: 0,
            pending_withdrawal_value: 0,
            has_pending_withdrawal: false,
            lifetime_net_loss: 0,
            redeemable_sweeps_coins: 0,
            open_support_tickets: 0
        };

        // Grouped Loyalty
        this.loyalty = data.loyalty || {
            tier_name: "",
            current_points: "0",
            lifetime_points: "0",
            points_to_next_tier: "0",
            day_streak: 0
        };

        // Referral source (affiliate or RAF)
        this.referral_source = data.referral_source || {};

        // User Flags (from backend)
        this.flags = data.flags || {
            has_rg_break: false,
            has_block: false,
            is_suspended: false,
            kyc_failed: false
        };
    }
}

export class StorePackage {
    constructor(
        id,
        brand_id,
        name,
        description,
        price,
        value,
        presale_price,
        icon_url,
        is_active,
        is_purchase_offer,
        is_repurchase_offer,
        external_id,
        entitlements,
        add_ons,
        free_spin_add_ons,
        lucky_wheel_add_ons,
        promo_credit_template_add_ons,
        bonus_credit_pack_add_ons,
        tags
    ) {
        this.id = id;
        this.brand_id = brand_id;
        this.name = name;
        this.description = description;
        this.price = price;
        this.value = value;
        this.presale_price = presale_price;
        this.icon_url = icon_url;
        this.is_active = is_active;
        this.is_purchase_offer = is_purchase_offer;
        this.is_repurchase_offer = is_repurchase_offer;
        this.external_id = external_id;
        this.entitlements = entitlements || [];
        this.add_ons = add_ons || [];
        this.free_spin_add_ons = free_spin_add_ons || [];
        this.lucky_wheel_add_ons = lucky_wheel_add_ons || [];
        this.promo_credit_template_add_ons = promo_credit_template_add_ons || [];
        this.bonus_credit_pack_add_ons = bonus_credit_pack_add_ons || [];
        this.tags = tags || [];
    }
}

export class PurchaseOffer {
    constructor(
        id,
        brand_id,
        name,
        description,
        price,
        value,
        presale_price,
        icon_url,
        is_active,
        is_purchase_offer,
        is_repurchase_offer,
        external_id,
        entitlements,
        add_ons,
        free_spin_add_ons,
        lucky_wheel_add_ons,
        promo_credit_template_add_ons,
        bonus_credit_pack_add_ons,
        tags,
        start_date,
        end_date,
        recurring_purchase_count
    ) {
        this.id = id;
        this.brand_id = brand_id;
        this.name = name;
        this.description = description;
        this.price = price;
        this.value = value;
        this.presale_price = presale_price;
        this.icon_url = icon_url;
        this.is_active = is_active;
        this.is_purchase_offer = is_purchase_offer;
        this.is_repurchase_offer = is_repurchase_offer;
        this.external_id = external_id;
        this.entitlements = entitlements || [];
        this.add_ons = add_ons || [];
        this.free_spin_add_ons = free_spin_add_ons || [];
        this.lucky_wheel_add_ons = lucky_wheel_add_ons || [];
        this.promo_credit_template_add_ons = promo_credit_template_add_ons || [];
        this.bonus_credit_pack_add_ons = bonus_credit_pack_add_ons || [];
        this.tags = tags || [];
        this.start_date = start_date;
        this.end_date = end_date;
        this.recurring_purchase_count = recurring_purchase_count;
    }
}

export class PurchaseRequest {
    constructor(data) {
        this.id = data.id;
        this.brand_user_id = data.brand_user_id;
        this.product_id = data.product_id;
        this.amount = data.amount;
        this.status = data.status;
        this.response = data.response;
        this.created_at = data.created_at;
        this.updated_at = data.updated_at;
        this.user_email = data.user_email;
        this.product_name = data.product_name;
        this.provider_name = data.provider_name;
        this.currency_code = data.currency_code;
        this.external_id = data.external_id;

        // Additional fields from search results
        this.merchant_transaction_id = data.merchant_transaction_id;
        this.user_display_name = data.user_display_name;
        this.provider_code = data.provider_code;
    }
}

export class RedemptionRequest {
    constructor(data) {
        this.id = data.id;
        this.brand_user_id = data.brand_user_id;
        this.amount = data.amount;
        this.status = data.status;
        this.created_at = data.created_at;
        this.user_email = data.user_email;
        this.user_display_name = data.user_display_name;
        this.currency_code = data.currency_code;
        this.ref_id = data.ref_id;
    }
}

export class CurrencyMode {
    constructor(id, description, code) {
        this.id = id;
        this.description = description;
        this.code = code;
    }
}

export class KhkAdminService {
    constructor() {
        this.baseurl = import.meta.env.VITE_KHK_ADMIN_BASE_URL.replace(/\/+$/, "");
        this.assetUploadBaseUrl = import.meta.env.VITE_ASSET_UPLOAD_BASE_URL;
    }

    /**
     * Brand code for management write payloads: always the switcher-selected
     * brand — the same source the X-Brand-Code interceptor reads — so creates
     * and updates land under the brand the admin is looking at (#2122).
     * @returns {string} the active brand code (e.g., 'giddyup', 'casinox')
     */
    selectedBrandCode() {
        return store.state.selectedBrand?.code || "giddyup";
    }

    /**
     * Fetch the RBAC capabilities granted to the current admin (issue #1878).
     * The backend resolves these from the caller's Okta groups, so this is the
     * single source of truth for what the UI may show. Auth header is injected
     * by the global axios interceptor in AuthService.
     * @returns {Promise<string[]>} capability strings, or [] on failure
     */
    async fetchMyCapabilities() {
        const response = await axios.get(`${this.baseurl}/admin/me/capabilities`);
        return response.data?.capabilities ?? [];
    }

    // Helper method to construct full asset URLs from relative paths
    constructAssetUploadURL(pathOrURL) {
        if (!pathOrURL) return "";

        // If it's already a full URL, return as-is
        if (pathOrURL.startsWith("http://") || pathOrURL.startsWith("https://")) {
            return pathOrURL;
        }

        // Ensure base URL doesn't end with slash
        const baseUrl = this.assetUploadBaseUrl.replace(/\/$/, "");

        // Ensure path starts with slash
        const path = pathOrURL.startsWith("/") ? pathOrURL : "/" + pathOrURL;

        return baseUrl + path;
    }

    async fetchGameGroup(group_id) {
        let response;
        try {
            response = await axios.get(`${this.baseurl}/admin/game-group/${group_id}`);
        } catch (error) {
            return null;
        }
        return new GameGroup(response.data);
    }

    async deleteGameGroup(groupId) {
        try {
            const response = await axios.delete(`${this.baseurl}/admin/game-groups/${groupId}`);
            return true;
        } catch (error) {
            throw new Error(error?.response?.data?.message || "Failed to delete group");
        }
    }

    async createGameGroup(groupName) {
        let response;
        try {
            response = await axios.post(`${this.baseurl}/admin/upsert-game-group`, {
                groupName: groupName,
                brandCode: this.selectedBrandCode()
            });
            return response.data;
        } catch (error) {
            return null;
        }
    }
    async fetchGameGroupList() {
        let response;
        try {
            response = await axios.get(`${this.baseurl}/admin/game-groups`);
        } catch (error) {
            return [];
        }

        if (!response.data || response.data.length === 0) {
            return [];
        }

        const gameGroups = [];
        for (let gameGroup of response.data) {
            gameGroups.push(
                new GameGroupListItem(
                    gameGroup.group_id,
                    gameGroup.brand_id,
                    gameGroup.game_count,
                    gameGroup.group_name,
                    gameGroup.brand_name,
                    gameGroup.catalog_names
                )
            );
        }

        return gameGroups;
    }

    // Game Catalog Methods
    async fetchGameCatalogList() {
        let response;
        try {
            response = await axios.get(`${this.baseurl}/admin/game-catalogs`);
        } catch (error) {
            return [];
        }

        if (!response.data || response.data.length === 0) {
            return [];
        }

        const gameCatalogs = [];
        for (let catalog of response.data) {
            gameCatalogs.push(
                new GameCatalogListItem(
                    catalog.catalog_id,
                    catalog.catalog_name,
                    catalog.brand_id,
                    catalog.brand_name,
                    catalog.is_default,
                    catalog.group_count,
                    catalog.start_time,
                    catalog.end_time
                )
            );
        }

        return gameCatalogs;
    }

    async createGameCatalog(catalogName) {
        let response;
        try {
            response = await axios.post(`${this.baseurl}/admin/create-game-catalog`, {
                catalogName: catalogName,
                brandCode: this.selectedBrandCode()
            });
            return response.data;
        } catch (error) {
            throw new Error(error?.response?.data?.message || "Failed to create catalog");
        }
    }

    async deleteGameCatalog(catalogId) {
        try {
            const response = await axios.delete(`${this.baseurl}/admin/game-catalogs/${catalogId}`);
            return true;
        } catch (error) {
            throw new Error(error?.response?.data?.message || "Failed to delete catalog");
        }
    }

    async fetchGameCatalogById(catalogId) {
        try {
            const response = await axios.get(`${this.baseurl}/admin/game-catalog/${catalogId}`);
            return new GameCatalogListItem(
                response.data.catalog_id,
                response.data.catalog_name,
                response.data.brand_id,
                response.data.brand_name,
                response.data.is_default,
                response.data.group_count,
                response.data.start_time,
                response.data.end_time
            );
        } catch (error) {
            throw new Error(error?.response?.data?.message || "Failed to fetch catalog details");
        }
    }

    async updateGameCatalog(catalogId, data) {
        try {
            const response = await axios.patch(`${this.baseurl}/admin/game-catalog/${catalogId}`, data);
            return response.data;
        } catch (error) {
            throw new Error(error?.response?.data?.message || "Failed to update catalog");
        }
    }

    async renameGameGroup(groupId, groupName) {
        try {
            const response = await axios.patch(`${this.baseurl}/admin/game-group/${groupId}`, {
                groupName: groupName
            });
            return response.data;
        } catch (error) {
            throw new Error(error?.response?.data?.message || "Failed to rename game group");
        }
    }

    async fetchCatalogCategories(catalogId) {
        try {
            const response = await axios.get(`${this.baseurl}/admin/game-catalog/${catalogId}/categories`);
            return response.data || [];
        } catch (error) {
            console.error("Error fetching catalog categories:", error);
            throw new Error(error?.response?.data?.message || "Failed to fetch catalog categories");
        }
    }

    async fetchAvailableGameCategories(catalogId) {
        try {
            const response = await axios.get(`${this.baseurl}/admin/game-catalog/${catalogId}/available-categories`);
            return response.data || [];
        } catch (error) {
            console.error("Error fetching available categories:", error);
            throw new Error(error?.response?.data?.message || "Failed to fetch available categories");
        }
    }

    async addCategoryToCatalog(catalogId, groupId) {
        try {
            const response = await axios.post(`${this.baseurl}/admin/game-catalog/${catalogId}/add-category`, {
                group_id: groupId
            });
            return response.data;
        } catch (error) {
            console.error("Error adding category to catalog:", error);
            throw new Error(error?.response?.data?.message || "Failed to add category to catalog");
        }
    }

    async removeCategoryFromCatalog(catalogId, groupId) {
        try {
            const response = await axios.delete(
                `${this.baseurl}/admin/game-catalog/${catalogId}/remove-category/${groupId}`
            );
            return response.data;
        } catch (error) {
            console.error("Error removing category from catalog:", error);
            throw new Error(error?.response?.data?.message || "Failed to remove category from catalog");
        }
    }

    async reorderCatalogCategories(catalogId, brandId, categoryOrders) {
        try {
            const response = await axios.post(`${this.baseurl}/admin/reorder-catalog-categories`, {
                catalogId: String(catalogId),
                brandId: String(brandId),
                ordering: categoryOrders.map(order => ({
                    groupId: String(order.groupId),
                    sortOrder: Number(order.sortOrder)
                }))
            });
            return response.status === 200 || response.status === 204;
        } catch (error) {
            console.error("Error reordering catalog categories:", error);
            return false;
        }
    }

    async generateS3PresignedURL(fileName, fileType, fileSize, purpose) {
        try {
            const response = await axios.post(`${this.baseurl}/admin/generate-s3-presigned-url`, {
                fileName,
                fileType,
                fileSize,
                purpose
            });
            return response.data;
        } catch (error) {
            console.error("Error generating presigned URL:", error);
            throw new Error(error?.response?.data?.message || "Failed to generate upload URL");
        }
    }

    async uploadFileToS3(presignedURL, file) {
        try {
            // Use a clean axios instance to avoid the global auth interceptor
            // injecting the Okta JWT into the S3 request. Presigned URLs provide
            // their own auth via query params — an extra Authorization header causes
            // S3 to reject the request with 400.
            const s3Axios = axios.create();
            await s3Axios.put(presignedURL, file, {
                headers: {
                    "Content-Type": file.type
                }
            });
            return true;
        } catch (error) {
            console.error("Error uploading file to S3:", error);
            throw new Error("Failed to upload file");
        }
    }

    // Represents a single game search result
    static GameSearchResult = class {
        constructor({ game_id, provider_id, studio_id, created_at, name }) {
            this.game_id = game_id;
            this.provider_id = provider_id;
            this.studio_id = studio_id;
            this.created_at = created_at;
            this.name = name;
        }
    };

    static GameVersion = class {
        constructor({ version_id, provider_game_id, version_created_at }) {
            this.version_id = version_id;
            this.provider_game_id = provider_game_id;
            this.version_created_at = version_created_at;
        }
    };

    async getGameVersions(gameId) {
        if (!gameId) return [];
        try {
            const response = await axios.get(`${this.baseurl}/admin/game-group/get-game-versions`, {
                params: { gameId }
            });
            if (!Array.isArray(response.data)) return [];
            return response.data.map(v => new KhkAdminService.GameVersion(v));
        } catch (error) {
            return [];
        }
    }

    async upsertGameGroupAssociation(groupId, versionId, brandId, displayOrder = null) {
        try {
            const payload = {
                groupId: String(groupId),
                versionId: String(versionId),
                brandId: String(brandId)
            };

            // Only include displayOrder if it's specified
            if (displayOrder !== null && displayOrder !== undefined) {
                payload.displayOrder = Number(displayOrder);
            }

            const response = await axios.post(`${this.baseurl}/admin/upsert-game-group-association`, payload);
            return response.status === 200 || response.status === 204;
        } catch (error) {
            return false;
        }
    }

    async deleteGameGroupAssociation(groupId, versionId, brandId) {
        try {
            const response = await axios.delete(
                `${this.baseurl}/admin/game-group-associations/${groupId}/${versionId}`
            );
            return response.status === 200 || response.status === 204;
        } catch (error) {
            return false;
        }
    }

    async reorderGameGroupGames(groupId, brandId, gameOrders) {
        try {
            const response = await axios.post(`${this.baseurl}/admin/reorder-game-group-games`, {
                groupId: String(groupId),
                brandId: String(brandId),
                ordering: gameOrders.map(order => ({
                    versionId: String(order.versionId),
                    displayOrder: Number(order.displayOrder)
                }))
            });
            return response.status === 200 || response.status === 204;
        } catch (error) {
            console.error("Error reordering game group games:", error);
            return false;
        }
    }

    async listAllGames() {
        try {
            const response = await axios.get(`${this.baseurl}/admin/all-games-order`);
            return Array.isArray(response.data) ? response.data : [];
        } catch (error) {
            console.error("Error listing All Games:", error);
            return [];
        }
    }

    async reorderAllGames(gameOrders) {
        try {
            const response = await axios.post(`${this.baseurl}/admin/all-games-order/reorder`, {
                ordering: gameOrders.map(order => ({
                    versionId: String(order.versionId),
                    displayOrder: Number(order.displayOrder)
                }))
            });
            return response.status === 200 || response.status === 204;
        } catch (error) {
            console.error("Error reordering All Games:", error);
            return false;
        }
    }

    async searchGames(searchTerm) {
        if (typeof searchTerm !== "string" || searchTerm.length < 3) {
            return [];
        }
        try {
            const response = await axios.get(`${this.baseurl}/admin/search-all-games`, {
                params: {
                    searchTerm: searchTerm,
                    khkOffset: 0,
                    maxVals: 30
                }
            });

            // Expecting { MetaData: { TotalSize: ... }, Results: [...] }
            if (!response.data || !Array.isArray(response.data.Results)) {
                return [];
            }
            const mapped = response.data.Results.map(g => new KhkAdminService.GameSearchResult(g));
            return mapped;
        } catch (error) {
            return [];
        }
    }

    /**
     * Fetch user list with optional search parameters
     * @param {Object} searchParams - Optional search parameters
     * @param {string} searchParams.email - Email to search (uses blind indexing server-side)
     * @param {number} searchParams.brand_user_id - Exact brand user ID to match
     * @param {string} searchParams.display_name - Display name partial match
     */
    async fetchUserList(searchParams = {}, page = 1, limit = 20, sortField = null, sortOrder = null) {
        // Build query params from search params
        const params = { page, limit };
        if (searchParams.email) {
            params.email = searchParams.email;
        }
        if (searchParams.brand_user_id) {
            params.brand_user_id = searchParams.brand_user_id;
        }
        if (searchParams.display_name) {
            params.display_name = searchParams.display_name;
        }
        if (searchParams.name) {
            params.name = searchParams.name;
        }
        if (searchParams.phone) {
            params.phone = searchParams.phone;
        }
        if (searchParams.verification_status) {
            params.verification_status = searchParams.verification_status;
        }
        if (searchParams.user_flag) {
            params.user_flag = searchParams.user_flag;
        }
        if (searchParams.external_id) {
            params.external_id = searchParams.external_id;
        }
        if (searchParams.brand_user_ids) {
            params.brand_user_ids = searchParams.brand_user_ids;
        }
        if (searchParams.start_date) {
            // Format Date object to YYYY-MM-DD string
            const d = new Date(searchParams.start_date);
            params.start_date = d.toISOString().split('T')[0];
        }
        if (searchParams.end_date) {
            const d = new Date(searchParams.end_date);
            params.end_date = d.toISOString().split('T')[0];
        }
        if (sortField) {
            params.sort_field = sortField;
        }
        if (sortOrder) {
            params.sort_order = sortOrder;
        }
        if (searchParams.pii_search_limit) {
            params.pii_search_limit = searchParams.pii_search_limit;
        }

        const response = await axios.get(`${this.baseurl}/admin/user`, { params });

        if (!response.data || !response.data.data || response.data.data.length === 0) {
            return { users: [], total: response.data?.total || 0 };
        }

        return {
            users: response.data.data,
            total: response.data.total,
            piiSearchContext: response.data.pii_search_context || null
        };
    }

    async fetchBrandUserOverview(brandUserId) {
        try {
            const response = await axios.get(`${this.baseurl}/admin/user/${brandUserId}`);
            return new BrandUserOverview(response.data);
        } catch (error) {
            console.error("Failed to fetch brand user overview:", error);
            return null;
        }
    }

    /**
     * Fetch PII data for a user (requires pii_viewer_role or super_admin_role)
     * Returns null if PII access is denied or user not found
     */
    async fetchUserPII(brandUserId) {
        try {
            const response = await axios.get(`${this.baseurl}/admin/users/${brandUserId}/pii`);
            return response.data;
        } catch (error) {
            // 403 means permission denied - return null silently
            if (error.response?.status === 403) {
                console.debug("PII access denied for user:", brandUserId);
                return null;
            }
            // 404 means no PII record exists
            if (error.response?.status === 404) {
                console.debug("No PII record for user:", brandUserId);
                return null;
            }
            console.error("Failed to fetch user PII:", error);
            return null;
        }
    }

    /**
     * Update PII data for a user (requires super_admin_role)
     * @param {number} brandUserId - The brand user ID
     * @param {Object} piiData - PII fields to update
     * @param {string} piiData.first_name - First name
     * @param {string} piiData.last_name - Last name
     * @param {string} piiData.dob - Date of birth (YYYY-MM-DD)
     * @param {string} piiData.phone - Phone number
     * @param {string} piiData.address1 - Address line 1
     * @param {string} piiData.address2 - Address line 2
     * @param {string} piiData.city - City
     * @param {string} piiData.state - State
     * @param {string} piiData.zip - ZIP code
     * @returns {Promise<Object>} Update confirmation
     */
    async updateUserPII(brandUserId, piiData) {
        try {
            const response = await axios.put(`${this.baseurl}/admin/users/${brandUserId}/pii`, piiData);
            return response.data;
        } catch (error) {
            if (error.response?.status === 403) {
                throw new Error("You don't have permission to edit user PII. Super admin role required.");
            }
            console.error("Failed to update user PII:", error);
            throw new Error(error?.response?.data?.message || "Failed to update user PII");
        }
    }

    /**
     * Add a manual flag to a user
     * Valid flags: bonus_abuser, underage_explicit, unwanted, vip, unlinked_account, test_account
     */
    async addUserFlag(brandUserId, flag) {
        try {
            const response = await axios.post(`${this.baseurl}/admin/users/${brandUserId}/flags`, { flag });
            return response.data;
        } catch (error) {
            if (error.response?.status === 403) {
                throw new Error("You don't have permission to modify user flags. Super admin role required.");
            }
            console.error("Failed to add user flag:", error);
            throw new Error(error?.response?.data?.message || "Failed to add user flag");
        }
    }

    /**
     * Remove a manual flag from a user
     */
    async removeUserFlag(brandUserId, flag) {
        try {
            const response = await axios.delete(`${this.baseurl}/admin/users/${brandUserId}/flags/${flag}`);
            return response.data;
        } catch (error) {
            if (error.response?.status === 403) {
                throw new Error("You don't have permission to modify user flags. Super admin role required.");
            }
            console.error("Failed to remove user flag:", error);
            throw new Error(error?.response?.data?.message || "Failed to remove user flag");
        }
    }

    /**
     * Update all manual flags for a user (replaces all existing flags)
     * Valid flags: bonus_abuser, underage_explicit, unwanted, vip, unlinked_account, test_account
     */
    async updateUserFlags(brandUserId, flags) {
        try {
            const response = await axios.put(`${this.baseurl}/admin/users/${brandUserId}/flags`, { flags });
            return response.data;
        } catch (error) {
            if (error.response?.status === 403) {
                throw new Error("You don't have permission to modify user flags. Super admin role required.");
            }
            console.error("Failed to update user flags:", error);
            throw new Error(error?.response?.data?.message || "Failed to update user flags");
        }
    }

    /**
     * Get list of valid manual user flags
     */
    async getValidUserFlags() {
        try {
            const response = await axios.get(`${this.baseurl}/admin/user-flags/valid`);
            return response.data.flags || [];
        } catch (error) {
            console.error("Failed to fetch valid user flags:", error);
            return [];
        }
    }

    async fetchUserFreeSpinAssignments(userId, page = 1, limit = 20) {
        let response;
        try {
            response = await axios.get(`${this.baseurl}/admin/users/${userId}/free-spin-assignments`, {
                params: { page, limit }
            });
        } catch (error) {
            return {
                assignments: [],
                total: 0,
                page: 1,
                limit: 20
            };
        }

        return response.data;
    }

    async fetchBrandUserTransactions(brandUserId) {
        let response;
        try {
            response = await axios.get(`${this.baseurl}/admin/user/${brandUserId}/transactions`);
        } catch (error) {
            return [];
        }

        return response.data;
    }

    async fetchGamesWithVersions() {
        let response;
        try {
            response = await axios.get(`${this.baseurl}/admin/games-with-versions`);
        } catch (error) {
            return [];
        }

        if (!response.data || response.data.length === 0) {
            return [];
        }

        // Backend now handles URL construction
        return response.data;
    }

    async fetchGameVersionById(versionId) {
        let response;
        try {
            response = await axios.get(`${this.baseurl}/admin/game-version/${versionId}`);
        } catch (error) {
            throw new Error("Failed to fetch game version details");
        }

        if (!response.data) {
            throw new Error("Game version not found");
        }

        // The backend now returns relative paths, construct full URLs for display
        const data = response.data;
        if (data.icon_path) {
            data.icon_path = this.constructAssetUploadURL(data.icon_path);
        }
        if (data.background_path) {
            data.background_path = this.constructAssetUploadURL(data.background_path);
        }

        return data;
    }

    async explainGameVersionEligibility(versionId, stateCode = "") {
        try {
            const response = await axios.get(`${this.baseurl}/admin/game-version/${versionId}/eligibility`, {
                params: stateCode ? { state: stateCode } : {}
            });
            return response.data;
        } catch (error) {
            throw new Error(error?.response?.data?.message || "Failed to explain game eligibility", { cause: error });
        }
    }

    async changeGameVersionPermission(versionId, scope, change) {
        try {
            const response = await axios.put(
                `${this.baseurl}/admin/game-version/${versionId}/permissions/${scope}`,
                change
            );
            return response.data;
        } catch (error) {
            throw new Error(error?.response?.data?.message || "Failed to change casino permission", { cause: error });
        }
    }

    async changeGameVersionRestriction(versionId, scope, change) {
        try {
            const response = await axios.put(
                `${this.baseurl}/admin/game-version/${versionId}/restrictions/${scope}`,
                change
            );
            return response.data;
        } catch (error) {
            throw new Error(error?.response?.data?.message || "Failed to change casino restriction", { cause: error });
        }
    }

    async fetchGameVersionGroups(versionId) {
        try {
            const response = await axios.get(`${this.baseurl}/admin/game-version/${versionId}/groups`);
            return response.data ?? [];
        } catch (error) {
            throw new Error(error?.response?.data?.message || "Failed to fetch game version categories");
        }
    }

    async generateGameLaunchURL(versionId) {
        const response = await axios.post(`${this.baseurl}/admin/game-version/${versionId}/launch-url`);
        return response.data;
    }

    async fetchGameProviders() {
        try {
            const response = await axios.get(`${this.baseurl}/admin/game-providers`);
            // Normalize to the shape used by the view
            return (response.data || []).map(p => ({
                id: p.provider_id,
                provider: p.display_name,
                code: p.provider_code,
                subProvider: null,
                enabled: p.enabled ?? true,
                lastUpdate: p.created_at
            }));
        } catch (error) {
            throw new Error(error?.response?.data?.message || "Failed to fetch game providers", { cause: error });
        }
    }

    async fetchGameStudios() {
        try {
            const response = await axios.get(`${this.baseurl}/admin/game-studios`);
            return (response.data || []).map(s => ({
                id: s.studio_id,
                name: s.studio_name,
                provider_id: s.provider_id,
                provider_name: s.provider_name,
                is_active: s.is_active,
                provider_is_active: s.provider_is_active
            }));
        } catch (error) {
            throw new Error(error?.response?.data?.message || "Failed to fetch game studios", { cause: error });
        }
    }

    async fetchPaymentProviders() {
        try {
            const response = await axios.get(`${this.baseurl}/admin/payment-providers`);
            // Normalize to the shape used by the view
            return (response.data || []).map(p => ({
                id: p.provider_id,
                provider: p.provider_name,
                code: p.provider_type,
                lastUpdate: p.created_at
            }));
        } catch (error) {
            return [];
        }
    }

    // Throws rather than returning [] on failure: the endpoint now resolves a
    // brand server-side and can genuinely fail, and an empty list is a valid
    // answer the caller must be able to tell apart from a dead request.
    async fetchEntitlementTypes() {
        try {
            const response = await axios.get(`${this.baseurl}/admin/store/entitlement-types`);
            return response.data || [];
        } catch (error) {
            const detail = error.response?.data?.message;
            throw new Error(detail || "Failed to fetch entitlement types", { cause: error });
        }
    }

    async updateGameVersion(versionId, updateData) {
        let response;
        try {
            response = await axios.put(`${this.baseurl}/admin/game-version/${versionId}`, updateData);
        } catch (error) {
            throw new Error("Failed to update game version");
        }

        return response.data;
    }

    async fetchGameLaunchOverrides(gameId) {
        let response;
        try {
            response = await axios.get(`${this.baseurl}/admin/games/${gameId}/launch-overrides`);
        } catch (error) {
            const detail = error.response?.data?.message;
            throw new Error(detail || "Failed to fetch launch overrides");
        }

        return response.data;
    }

    async updateGameLaunchOverrides(gameId, updateData) {
        let response;
        try {
            response = await axios.put(`${this.baseurl}/admin/games/${gameId}/launch-overrides`, updateData);
        } catch (error) {
            const detail = error.response?.data?.message;
            throw new Error(detail || "Failed to update launch overrides");
        }

        return response.data;
    }

    async fetchPendingRedemptions() {
        let response;
        try {
            response = await axios.get(`${this.baseurl}/admin/get-pending-redemptions`);
        } catch (error) {
            return [];
        }
        return response.data;
    }

    async fetchPurchaseRequests(page = 1, limit = 20) {
        let response;
        try {
            response = await axios.get(`${this.baseurl}/admin/purchase-requests`, {
                params: { page, limit }
            });
        } catch (error) {
            console.error("Failed to fetch purchase requests:", error);
            return { purchase_requests: [], total: 0 };
        }

        if (!response.data || !response.data.purchase_requests) {
            return { purchase_requests: [], total: 0 };
        }

        return {
            purchase_requests: response.data.purchase_requests.map(req => new PurchaseRequest(req)),
            total: response.data.total || 0
        };
    }

    async fetchReferralCodes(searchParams = {}, page = 1, limit = 20, sortField = null, sortOrder = null) {
        let response;
        try {
            const params = { page, limit };

            // Search fields
            if (searchParams.code) params.code = searchParams.code;
            if (searchParams.owner_email) params.owner_email = searchParams.owner_email;
            if (searchParams.brand_user_id) params.brand_user_id = searchParams.brand_user_id;

            // Date filters
            if (searchParams.created_at_from) params.created_at_from = searchParams.created_at_from;
            if (searchParams.created_at_to) params.created_at_to = searchParams.created_at_to;
            if (searchParams.last_used_from) params.last_used_from = searchParams.last_used_from;
            if (searchParams.last_used_to) params.last_used_to = searchParams.last_used_to;

            // Referral count filters
            if (searchParams.min_referrals) params.min_referrals = searchParams.min_referrals;
            if (searchParams.max_referrals) params.max_referrals = searchParams.max_referrals;

            // Sorting
            if (sortField) params.sort_field = sortField;
            if (sortOrder) params.sort_order = sortOrder;

            // Use search endpoint when filters or custom sort are present, otherwise use list endpoint
            const hasFilters = Object.values(searchParams).some(v => v !== null && v !== undefined && v !== '');
            const hasCustomSort = sortField && (sortField !== 'created_at' || sortOrder !== 'desc');

            if (hasFilters || hasCustomSort) {
                response = await axios.get(`${this.baseurl}/admin/referral-codes/search`, { params });
            } else {
                response = await axios.get(`${this.baseurl}/admin/referral-codes`, { params });
            }
        } catch (error) {
            console.error("Failed to fetch referral codes:", error);
            return { referral_codes: [], total: 0 };
        }

        // Normalize response — search returns "results", list returns "referral_codes"
        const codes = response.data?.results || response.data?.referral_codes || [];

        return {
            referral_codes: codes,
            total: response.data?.total || 0
        };
    }

    async getUserReferrals(userId, { limit = 20, page = 1, search } = {}) {
        let response;
        try {
            const params = { limit, page };
            if (search) params.search = search;
            response = await axios.get(`${this.baseurl}/admin/users/${userId}/referrals`, {
                params
            });
        } catch (error) {
            console.error("Failed to get user referrals:", error);
            return { referrals: [], total: 0 };
        }

        if (!response.data || !response.data.referrals) {
            return { referrals: [], total: 0 };
        }

        return {
            referrals: response.data.referrals,
            total: response.data.total || 0
        };
    }

    async searchRedemptionRequests(filters = {}) {
        let response;
        try {
            // Build query parameters from filters
            const params = new URLSearchParams();

            // Pagination
            if (filters.page) params.append("page", filters.page);
            if (filters.limit) params.append("limit", filters.limit);

            // Sorting
            if (filters.sort_field) params.append("sort_field", filters.sort_field);
            if (filters.sort_order) params.append("sort_order", filters.sort_order);

            // Filters
            if (filters.status) params.append("status", filters.status);
            if (filters.brand_user_id) params.append("brand_user_id", filters.brand_user_id);
            if (filters.start_date) params.append("start_date", filters.start_date);
            if (filters.end_date) params.append("end_date", filters.end_date);
            if (filters.min_amount) params.append("min_amount", filters.min_amount);
            if (filters.max_amount) params.append("max_amount", filters.max_amount);

            // Search fields - URLSearchParams handles encoding automatically
            if (filters.user_email) params.append("user_email", filters.user_email);
            if (filters.user_display_name) params.append("user_display_name", filters.user_display_name);
            if (filters.ref_id) params.append("ref_id", filters.ref_id);

            response = await axios.get(`${this.baseurl}/admin/redemption-requests/search?${params.toString()}`);
        } catch (error) {
            console.error("Failed to search redemption requests:", error);
            return { results: [], total: 0 };
        }

        if (!response.data || !response.data.results) {
            return { results: [], total: 0 };
        }

        return {
            results: response.data.results.map(req => new RedemptionRequest(req)),
            total: response.data.total || 0
        };
    }

    async fetchPhoneVerifications(page = 1, limit = 20) {
        let response;
        try {
            response = await axios.get(`${this.baseurl}/admin/phone-verifications`, {
                params: { page, limit }
            });
        } catch (error) {
            console.error("Failed to fetch phone verifications:", error);
            return { verifications: [], total: 0 };
        }

        if (!response.data || !response.data.verifications) {
            return { verifications: [], total: 0 };
        }

        return {
            verifications: response.data.verifications,
            total: response.data.total || 0
        };
    }

    async searchPhoneVerifications(filters = {}) {
        let response;
        try {
            // Build query parameters from filters
            const params = new URLSearchParams();

            // Pagination
            if (filters.page) params.append("page", filters.page);
            if (filters.limit) params.append("limit", filters.limit);

            // Sorting
            if (filters.sort_field) params.append("sort_field", filters.sort_field);
            if (filters.sort_order) params.append("sort_order", filters.sort_order);

            // Filters
            if (filters.status) params.append("status", filters.status);
            if (filters.brand_user_id) params.append("brand_user_id", filters.brand_user_id);
            if (filters.code_sent_from) params.append("code_sent_from", filters.code_sent_from);
            if (filters.code_sent_to) params.append("code_sent_to", filters.code_sent_to);

            // Search fields - URLSearchParams handles encoding automatically
            if (filters.user_email) params.append("user_email", filters.user_email);
            if (filters.user_display_name) params.append("user_display_name", filters.user_display_name);
            if (filters.pii_search_limit) params.append("pii_search_limit", filters.pii_search_limit);

            response = await axios.get(`${this.baseurl}/admin/phone-verifications/search?${params.toString()}`);
        } catch (error) {
            console.error("Failed to search phone verifications:", error);
            return { results: [], total: 0 };
        }

        if (!response.data || !response.data.results) {
            return { results: [], total: 0 };
        }

        return {
            results: response.data.results,
            total: response.data.total || 0,
            pii_search_context: response.data.pii_search_context || null
        };
    }

    async fetchKycVerifications(page = 1, limit = 20) {
        let response;
        try {
            response = await axios.get(`${this.baseurl}/admin/kyc-verifications`, {
                params: { page, limit }
            });
        } catch (error) {
            console.error("Failed to fetch KYC verifications:", error);
            return { verifications: [], total: 0 };
        }

        if (!response.data || !response.data.verifications) {
            return { verifications: [], total: 0 };
        }

        return {
            verifications: response.data.verifications,
            total: response.data.total || 0
        };
    }

    /**
     * Fetches verification queue (combined phone and KYC verifications)
     * @param {number} page - Page number (1-indexed)
     * @param {number} limit - Number of items per page
     * @returns {Promise<{verifications: Array, total: number}>}
     */
    async fetchVerifications(page = 1, limit = 20) {
        let response;
        try {
            response = await axios.get(`${this.baseurl}/admin/verifications`, {
                params: { page, limit }
            });
        } catch (error) {
            console.error("Failed to fetch unified verifications:", error);
            return { verifications: [], total: 0 };
        }

        if (!response.data || !response.data.verifications) {
            return { verifications: [], total: 0 };
        }

        return {
            verifications: response.data.verifications,
            total: response.data.total || 0
        };
    }

    /**
     * Searches verification queue with filters
     * @param {Object} filters - Search filters
     * @returns {Promise<{verifications: Array, total: number}>}
     */
    async searchVerifications(filters = {}) {
        let response;
        try {
            const params = new URLSearchParams();

            // Pagination
            if (filters.page) params.append("page", filters.page);
            if (filters.limit) params.append("limit", filters.limit);

            // Sorting
            if (filters.sort_field) params.append("sort_field", filters.sort_field);
            if (filters.sort_order) params.append("sort_order", filters.sort_order);

            // Filters
            if (filters.status) params.append("status", filters.status);
            if (filters.brand_user_id) params.append("brand_user_id", filters.brand_user_id);
            if (filters.user_email) params.append("user_email", filters.user_email);
            if (filters.user_display_name) params.append("user_display_name", filters.user_display_name);

            response = await axios.get(`${this.baseurl}/admin/verifications/search?${params.toString()}`);
        } catch (error) {
            console.error("Failed to search verifications:", error);
            return { verifications: [], total: 0 };
        }

        if (!response.data) {
            return { verifications: [], total: 0 };
        }

        return {
            verifications: response.data.results || response.data.verifications || [],
            total: response.data.total || 0
        };
    }

    async searchKycVerifications(filters = {}) {
        let response;
        try {
            // Build query parameters from filters
            const params = new URLSearchParams();

            // Pagination
            if (filters.page) params.append("page", filters.page);
            if (filters.limit) params.append("limit", filters.limit);

            // Sorting
            if (filters.sort_field) params.append("sort_field", filters.sort_field);
            if (filters.sort_order) params.append("sort_order", filters.sort_order);

            // Filters
            if (filters.status) params.append("status", filters.status);
            if (filters.brand_user_id) params.append("brand_user_id", filters.brand_user_id);
            if (filters.assigned_to) params.append("assigned_to", filters.assigned_to);
            if (filters.request_from) params.append("request_from", filters.request_from);
            if (filters.request_to) params.append("request_to", filters.request_to);

            // Search fields - URLSearchParams handles encoding automatically
            if (filters.user_email) params.append("user_email", filters.user_email);
            if (filters.user_display_name) params.append("user_display_name", filters.user_display_name);

            response = await axios.get(`${this.baseurl}/admin/kyc-verifications/search?${params.toString()}`);
        } catch (error) {
            console.error("Failed to search KYC verifications:", error);
            // `error` tells a failed search from an empty one (#2586).
            return { results: [], total: 0, meta: {}, error };
        }

        if (!response.data) {
            return { results: [], total: 0, meta: {} };
        }

        return {
            results: response.data.results || [],
            total: response.data.total || 0,
            meta: response.data.meta || {}
        };
    }

    async getVerificationStatuses() {
        try {
            const response = await axios.get(`${this.baseurl}/admin/verification-statuses`);
            return response.data.statuses || [];
        } catch (error) {
            console.error("Failed to fetch verification statuses:", error);
            return [];
        }
    }

    async allowKycResubmit(brandUserId, reason = "") {
        const response = await axios.post(`${this.baseurl}/admin/kyc/allow-resubmit`, {
            brand_user_id: brandUserId,
            reason: reason
        });
        return response.data;
    }

    async approveKycVerification(verificationId, notes) {
        const response = await axios.post(`${this.baseurl}/admin/verifications/${verificationId}/approve`, {
            notes
        });
        return response.data;
    }

    async rejectKycVerification(verificationId, notes) {
        const response = await axios.post(`${this.baseurl}/admin/verifications/${verificationId}/reject`, {
            notes
        });
        return response.data;
    }

    async createAmwestAccount(brandUserId, notes = "") {
        const response = await axios.post(`${this.baseurl}/admin/amwest/create-account`, {
            brand_user_id: brandUserId,
            notes: notes
        });
        return response.data;
    }

    async directKycApprove(brandUserId, notes, piiData = {}) {
        const response = await axios.post(`${this.baseurl}/admin/kyc/direct-approve`, {
            brand_user_id: brandUserId,
            notes: notes,
            ...piiData
        });
        return response.data;
    }

    async assignVerification(verificationId, notes = "", assignToUserId = null) {
        try {
            const body = { notes };
            if (assignToUserId) body.assign_to_user_id = assignToUserId;
            const response = await axios.post(`${this.baseurl}/admin/verifications/${verificationId}/assign`, body);
            return response.data;
        } catch (error) {
            console.error("Failed to assign verification:", error);
            throw new Error(error?.response?.data?.message || "Failed to assign verification");
        }
    }

    async unassignVerification(verificationId, reason = "") {
        try {
            const response = await axios.post(`${this.baseurl}/admin/verifications/${verificationId}/unassign`, {
                reason
            });
            return response.data;
        } catch (error) {
            console.error("Failed to unassign verification:", error);
            throw new Error(error?.response?.data?.message || "Failed to unassign verification");
        }
    }

    async setVerificationOnHold(verificationId, onHold, reason = "") {
        try {
            const response = await axios.post(`${this.baseurl}/admin/verifications/${verificationId}/hold`, {
                on_hold: onHold,
                reason
            });
            return response.data;
        } catch (error) {
            console.error("Failed to update verification hold status:", error);
            throw new Error(error?.response?.data?.message || "Failed to update hold status");
        }
    }

    async fetchDocvRequests(brandUserId) {
        const response = await axios.get(`${this.baseurl}/admin/users/${brandUserId}/docv-requests`);
        return response.data;
    }

    async searchPurchaseRequests(filters = {}) {
        let response;
        try {
            // Build query parameters from filters
            const params = new URLSearchParams();

            // Pagination
            if (filters.page) params.append("page", filters.page);
            if (filters.limit) params.append("limit", filters.limit);

            // Sorting
            if (filters.sort_field) params.append("sort_field", filters.sort_field);
            if (filters.sort_order) params.append("sort_order", filters.sort_order);

            // Filters
            if (filters.product_id) params.append("product_id", filters.product_id);
            if (filters.status) params.append("status", filters.status);
            if (filters.provider_code) params.append("provider_code", filters.provider_code);
            if (filters.brand_user_id) params.append("brand_user_id", filters.brand_user_id);
            if (filters.start_date) params.append("start_date", filters.start_date);
            if (filters.end_date) params.append("end_date", filters.end_date);
            if (filters.min_amount) params.append("min_amount", filters.min_amount);
            if (filters.max_amount) params.append("max_amount", filters.max_amount);

            // Search fields - URLSearchParams handles encoding automatically
            if (filters.user_email) params.append("user_email", filters.user_email);
            if (filters.user_display_name) params.append("user_display_name", filters.user_display_name);
            if (filters.product_name) params.append("product_name", filters.product_name);
            if (filters.merchant_transaction_id)
                params.append("merchant_transaction_id", filters.merchant_transaction_id);

            response = await axios.get(`${this.baseurl}/admin/purchase-requests/search?${params.toString()}`);
        } catch (error) {
            console.error("Failed to search purchase requests:", error);
            return { results: [], total: 0 };
        }

        if (!response.data || !response.data.results) {
            return { results: [], total: 0 };
        }

        return {
            results: response.data.results.map(req => new PurchaseRequest(req)),
            total: response.data.total || 0
        };
    }

    // Fetches { total_count, total_value } for a user-detail summary card. Lets the
    // axios error propagate so the calling composable can render the failure — swallowing
    // it here would render a real "0 / $0" indistinguishable from a successful empty
    // range and silently corrupt the average-purchase / average-deposit math.
    async _getUserTransactionTotals(brandUserId, slug, { startDate, endDate } = {}) {
        const params = new URLSearchParams();
        if (startDate) params.append("start_date", startDate);
        if (endDate) params.append("end_date", endDate);
        const qs = params.toString();
        const url = `${this.baseurl}/admin/user/${brandUserId}/${slug}${qs ? `?${qs}` : ""}`;

        const response = await axios.get(url);
        return {
            total_count: Number(response.data?.total_count ?? 0),
            total_value: response.data?.total_value ?? "0"
        };
    }

    getUserPurchaseTotals(brandUserId, range) {
        return this._getUserTransactionTotals(brandUserId, "purchase-totals", range);
    }

    getUserDepositTotals(brandUserId, range) {
        return this._getUserTransactionTotals(brandUserId, "deposit-totals", range);
    }

    getUserWithdrawalTotals(brandUserId, range) {
        return this._getUserTransactionTotals(brandUserId, "withdrawal-totals", range);
    }

    async fetchDepositRequests(page = 1, limit = 20) {
        let response;
        try {
            response = await axios.get(`${this.baseurl}/admin/deposit-requests`, {
                params: { page, limit }
            });
        } catch (error) {
            console.error("Failed to fetch deposit requests:", error);
            return { deposit_requests: [], total: 0 };
        }

        if (!response.data || !response.data.deposit_requests) {
            return { deposit_requests: [], total: 0 };
        }

        return {
            deposit_requests: response.data.deposit_requests,
            total: response.data.total || 0
        };
    }

    async searchDepositRequests(filters = {}) {
        let response;
        try {
            // Build query parameters from filters
            const params = new URLSearchParams();

            // Pagination
            if (filters.page) params.append("page", filters.page);
            if (filters.limit) params.append("limit", filters.limit);

            // Sorting
            if (filters.sort_field) params.append("sort_field", filters.sort_field);
            if (filters.sort_order) params.append("sort_order", filters.sort_order);

            // Filters
            if (filters.status) params.append("status", filters.status);
            if (filters.provider_code) params.append("provider_code", filters.provider_code);
            if (filters.brand_user_id) params.append("brand_user_id", filters.brand_user_id);
            if (filters.start_date) params.append("start_date", filters.start_date);
            if (filters.end_date) params.append("end_date", filters.end_date);
            if (filters.min_amount) params.append("min_amount", filters.min_amount);
            if (filters.max_amount) params.append("max_amount", filters.max_amount);

            // Search fields - URLSearchParams handles encoding automatically
            if (filters.user_email) params.append("user_email", filters.user_email);
            if (filters.user_display_name) params.append("user_display_name", filters.user_display_name);
            if (filters.khk_transaction_id)
                params.append("khk_transaction_id", filters.khk_transaction_id);
            if (filters.merchant_transaction_id)
                params.append("merchant_transaction_id", filters.merchant_transaction_id);
            if (filters.pii_search_limit) params.append("pii_search_limit", filters.pii_search_limit);

            response = await axios.get(`${this.baseurl}/admin/deposit-requests/search?${params.toString()}`);
        } catch (error) {
            console.error("Failed to search deposit requests:", error);
            return { results: [], total: 0 };
        }

        if (!response.data || !response.data.results) {
            return { results: [], total: 0 };
        }

        return {
            results: response.data.results,
            total: response.data.total || 0,
            pii_search_context: response.data.pii_search_context || null
        };
    }

    async getDepositRequestDetail(id) {
        try {
            const response = await axios.get(`${this.baseurl}/admin/deposit-requests/${id}`);
            return response.data;
        } catch (error) {
            console.error("Failed to get deposit request detail:", error);
            throw new Error(error?.response?.data?.message || "Failed to get deposit request detail");
        }
    }

    async getPurchaseRequestDetail(id) {
        try {
            const response = await axios.get(`${this.baseurl}/admin/purchase-requests/${id}`);
            return response.data;
        } catch (error) {
            console.error("Failed to get purchase request detail:", error);
            throw new Error(error?.response?.data?.message || "Failed to get purchase request detail");
        }
    }

    async repairStuckDeposit(depositId, reason) {
        try {
            const response = await axios.post(`${this.baseurl}/admin/deposit-requests/${depositId}/action`, {
                action: 'repair',
                reason
            });
            return response.data;
        } catch (error) {
            console.error("Failed to repair stuck deposit:", error);
            throw new Error(error?.response?.data?.message || "Failed to repair stuck deposit");
        }
    }

    async syncDepositRequest(depositId) {
        try {
            const response = await axios.post(`${this.baseurl}/admin/deposit-requests/${depositId}/sync`);
            return response.data;
        } catch (error) {
            console.error("Failed to sync deposit:", error);
            throw new Error(error?.response?.data?.message || "Failed to sync deposit");
        }
    }

    async fetchPurchaseTransactions(page = 1, limit = 20) {
        let response;
        try {
            response = await axios.get(`${this.baseurl}/admin/purchase-transactions`, {
                params: { page, limit }
            });
        } catch (error) {
            console.error("Failed to fetch purchase transactions:", error);
            return { purchase_transactions: [], total: 0 };
        }

        if (!response.data || !response.data.purchase_transactions) {
            return { purchase_transactions: [], total: 0 };
        }

        return {
            purchase_transactions: response.data.purchase_transactions,
            total: response.data.total || 0
        };
    }

    async searchPurchaseTransactions(filters = {}) {
        let response;
        try {
            // Build query parameters from filters
            const params = new URLSearchParams();

            // Pagination
            if (filters.page) params.append("page", filters.page);
            if (filters.limit) params.append("limit", filters.limit);

            // Sorting
            if (filters.sort_field) params.append("sort_field", filters.sort_field);
            if (filters.sort_order) params.append("sort_order", filters.sort_order);

            // Filters
            if (filters.product_id) params.append("product_id", filters.product_id);
            if (filters.status) params.append("status", filters.status);
            if (filters.brand_user_id) params.append("brand_user_id", filters.brand_user_id);
            if (filters.start_date) params.append("start_date", filters.start_date);
            if (filters.end_date) params.append("end_date", filters.end_date);
            if (filters.min_amount) params.append("min_amount", filters.min_amount);
            if (filters.max_amount) params.append("max_amount", filters.max_amount);

            // Search fields - URLSearchParams handles encoding automatically
            if (filters.user_email) params.append("user_email", filters.user_email);
            if (filters.user_display_name) params.append("user_display_name", filters.user_display_name);
            if (filters.package_name) params.append("package_name", filters.package_name);
            if (filters.khk_transaction_id)
                params.append("khk_transaction_id", filters.khk_transaction_id);
            if (filters.merchant_transaction_id)
                params.append("merchant_transaction_id", filters.merchant_transaction_id);
            if (filters.pii_search_limit) params.append("pii_search_limit", filters.pii_search_limit);

            response = await axios.get(`${this.baseurl}/admin/purchase-transactions/search?${params.toString()}`);
        } catch (error) {
            console.error("Failed to search purchase transactions:", error);
            return { results: [], total: 0 };
        }

        if (!response.data || !response.data.results) {
            return { results: [], total: 0 };
        }

        return {
            results: response.data.results,
            total: response.data.total || 0,
            pii_search_context: response.data.pii_search_context || null
        };
    }

    // searchInactivityFeeAssessments fetches the #1268 inactivity-fee history
    // report (brand-scoped server-side). Returns the page of rows, total matching
    // count, and the summed charged amount across all matches.
    async searchInactivityFeeAssessments(filters = {}) {
        let response;
        try {
            const params = new URLSearchParams();
            if (filters.page) params.append("page", filters.page);
            if (filters.limit) params.append("limit", filters.limit);
            if (filters.sort_field) params.append("sort_field", filters.sort_field);
            if (filters.sort_order) params.append("sort_order", filters.sort_order);
            if (filters.status) params.append("status", filters.status);
            if (filters.brand_user_id) params.append("brand_user_id", filters.brand_user_id);
            if (filters.period_start) params.append("period_start", filters.period_start);
            if (filters.period_end) params.append("period_end", filters.period_end);

            response = await axios.get(
                `${this.baseurl}/admin/inactivity-fee-assessments/search?${params.toString()}`
            );
        } catch (error) {
            console.error("Failed to search inactivity fee assessments:", error);
            return { results: [], total: 0, total_charged: "0" };
        }

        if (!response.data || !response.data.results) {
            return { results: [], total: 0, total_charged: "0" };
        }

        return {
            results: response.data.results,
            total: response.data.total || 0,
            total_charged: response.data.total_charged || "0"
        };
    }

    async searchDesyncQueue(filters = {}) {
        let response;
        try {
            const params = new URLSearchParams();

            // Pagination
            if (filters.page) params.append("page", filters.page);
            if (filters.limit) params.append("limit", filters.limit);

            // Sorting
            if (filters.sort_field) params.append("sort_field", filters.sort_field);
            if (filters.sort_order) params.append("sort_order", filters.sort_order);

            // Filters
            if (filters.brand_user_id) params.append("brand_user_id", filters.brand_user_id);
            if (filters.user_email) params.append("user_email", filters.user_email);
            if (filters.user_display_name) params.append("user_display_name", filters.user_display_name);
            if (filters.min_stuck_minutes) params.append("min_stuck_minutes", filters.min_stuck_minutes);
            if (filters.min_amount) params.append("min_amount", filters.min_amount);
            if (filters.max_amount) params.append("max_amount", filters.max_amount);
            if (filters.pii_search_limit) params.append("pii_search_limit", filters.pii_search_limit);

            response = await axios.get(`${this.baseurl}/admin/desync-queue/search?${params.toString()}`);
        } catch (error) {
            console.error("Failed to search desync queue:", error);
            return { results: [], total: 0 };
        }

        if (!response.data || !response.data.results) {
            return { results: [], total: 0 };
        }

        return {
            results: response.data.results,
            total: response.data.total || 0,
            pii_search_context: response.data.pii_search_context || null
        };
    }

    async fetchPurchaseTransactionBetDetails(purchaseId) {
        try {
            const response = await axios.get(`${this.baseurl}/admin/purchase-transactions/${purchaseId}/bet-details`);
            return response.data;
        } catch (error) {
            console.error("Failed to fetch bet details:", error);
            throw new Error(error?.response?.data?.message || "Failed to fetch bet details");
        }
    }

    async fetchPendingPurchaseBetDetails(purchaseRequestId) {
        try {
            const response = await axios.get(`${this.baseurl}/admin/pending-purchases/${purchaseRequestId}/bet-details`);
            return response.data;
        } catch (error) {
            console.error("Failed to fetch pending purchase bet details:", error);
            throw new Error(error?.response?.data?.message || "Failed to fetch bet details");
        }
    }

    async fetchRedemptionRequests(page = 1, limit = 20) {
        let response;
        try {
            response = await axios.get(`${this.baseurl}/admin/redemption-requests`, {
                params: { page, limit }
            });
        } catch (error) {
            console.error("Failed to fetch redemption requests:", error);
            return { redemption_requests: [], total: 0 };
        }
        if (!response.data || !response.data.redemption_requests) {
            return { redemption_requests: [], total: 0 };
        }
        return {
            redemption_requests: response.data.redemption_requests.map(req => new RedemptionRequest(req)),
            total: response.data.total || 0
        };
    }

    async setRedemptionInProgress(redemptionId, userId) {
        try {
            const response = await axios.post(`${this.baseurl}/admin/set-in-progress-redemption`, {
                redemptionId: redemptionId,
                action: "approve", // This endpoint just sets to in-progress
                userId: userId
            });
            return response.data;
        } catch (error) {
            console.error("Failed to set redemption in progress:", error);
            throw error;
        }
    }

    async settleRedemption(redemptionId, action, userId) {
        try {
            const response = await axios.post(`${this.baseurl}/admin/settle-redemption`, {
                redemptionId: redemptionId,
                action: action, // 'approved', 'rejected', 'settled', 'failed'
                userId: userId
            });
            return response.data;
        } catch (error) {
            console.error("Failed to settle redemption:", error);
            throw error;
        }
    }

    async fetchStorePackages() {
        let response;
        try {
            response = await axios.get(`${this.baseurl}/admin/store-packages`);
        } catch (error) {
            return [];
        }

        if (!response.data || !response.data.packages) {
            return [];
        }

        return response.data.packages.map(
            pkg =>
                new StorePackage(
                    pkg.id,
                    pkg.brand_id,
                    pkg.name,
                    pkg.description,
                    pkg.price,
                    pkg.value,
                    pkg.presale_price,
                    pkg.icon_url,
                    pkg.is_active,
                    pkg.is_purchase_offer,
                    pkg.is_repurchase_offer,
                    pkg.external_id,
                    pkg.entitlements,
                    pkg.add_ons,
                    pkg.free_spin_add_ons,
                    pkg.lucky_wheel_add_ons,
                    pkg.promo_credit_template_add_ons,
                    pkg.bonus_credit_pack_add_ons,
                    pkg.tags
                )
        );
    }

    async fetchStorePackageById(packageId) {
        const response = await axios.get(`${this.baseurl}/admin/store-packages/${packageId}`);
        const pkg = response.data;

        return new StorePackage(
            pkg.id,
            pkg.brand_id,
            pkg.name,
            pkg.description,
            pkg.price,
            pkg.value,
            pkg.presale_price,
            pkg.icon_url,
            pkg.is_active,
            pkg.is_purchase_offer,
            pkg.is_repurchase_offer,
            pkg.external_id,
            pkg.entitlements,
            pkg.add_ons,
            pkg.free_spin_add_ons,
            pkg.lucky_wheel_add_ons,
            pkg.promo_credit_template_add_ons,
            pkg.bonus_credit_pack_add_ons,
            pkg.tags
        );
    }

    async updateStorePackage(packageId, updateData) {
        const response = await axios.put(`${this.baseurl}/admin/store-packages/${packageId}`, updateData);
        return response.data;
    }

    async createStorePackage(packageData) {
        const response = await axios.post(`${this.baseurl}/admin/store-packages`, packageData);
        return response.data;
    }

    async deleteStorePackage(packageId) {
        const response = await axios.delete(`${this.baseurl}/admin/store-packages/${packageId}`);
        return response.data;
    }

    async createStoreEntitlement(packageId, entitlementData) {
        const response = await axios.post(
            `${this.baseurl}/admin/store-packages/${packageId}/entitlements`,
            entitlementData
        );
        return response.data;
    }

    async deleteStoreEntitlement(entitlementId) {
        const response = await axios.delete(`${this.baseurl}/admin/store-entitlements/${entitlementId}`);
        return response.data;
    }

    async createStoreAddOn(packageId, addOnData) {
        const response = await axios.post(`${this.baseurl}/admin/store-packages/${packageId}/add-ons`, addOnData);
        return response.data;
    }

    async createStoreFreeSpinAddOn(packageId, addOnData) {
        const response = await axios.post(
            `${this.baseurl}/admin/store-packages/${packageId}/free-spin-add-ons`,
            addOnData
        );
        return response.data;
    }

    async deleteStoreAddOn(addOnId) {
        const response = await axios.delete(`${this.baseurl}/admin/store-add-ons/${addOnId}`);
        return response.data;
    }

    async deleteStoreFreeSpinAddOn(addOnId) {
        const response = await axios.delete(`${this.baseurl}/admin/store-free-spin-add-ons/${addOnId}`);
        return response.data;
    }

    async fetchCurrencyModes() {
        try {
            const response = await axios.get(`${this.baseurl}/admin/currency-modes`);
            if (!response.data || !response.data.currency_modes || response.data.currency_modes.length === 0) {
                return [];
            }
            return response.data.currency_modes.map(mode => new CurrencyMode(mode.id, mode.description, mode.code));
        } catch (error) {
            console.error("Error fetching currency modes:", error);
            return [];
        }
    }

    async fetchTransactionTypes() {
        try {
            const response = await axios.get(`${this.baseurl}/admin/transaction-types`);
            if (!response.data || !response.data.types || response.data.types.length === 0) {
                return [];
            }
            return response.data.types;
        } catch (error) {
            console.error("Error fetching transaction types:", error);
            return [];
        }
    }

    // Lucky Wheel Spin Config methods
    async fetchLuckyWheelSpinConfigs() {
        try {
            const response = await axios.get(`${this.baseurl}/admin/lucky-wheel-configs`);
            if (!response.data) {
                return { configs: [] };
            }
            return response.data;
        } catch (error) {
            console.error("Error fetching lucky wheel spin configs:", error);
            throw error;
        }
    }

    async fetchLuckyWheelSpinConfigById(configId) {
        try {
            const response = await axios.get(`${this.baseurl}/admin/lucky-wheel-configs/${configId}`);
            if (!response.data || !response.data.config) {
                throw new Error("Lucky wheel spin config not found");
            }
            // Return the config with add_ons and loyalty_tiers merged
            return {
                ...response.data.config,
                add_ons: response.data.add_ons || [],
                loyalty_tiers: response.data.loyalty_tiers || []
            };
        } catch (error) {
            console.error("Error fetching lucky wheel spin config:", error);
            throw error;
        }
    }

    async updateLuckyWheelSpinConfig(configId, updateData) {
        try {
            const response = await axios.put(`${this.baseurl}/admin/lucky-wheel-configs/${configId}`, updateData);
            return response.data;
        } catch (error) {
            console.error("Error updating lucky wheel spin config:", error);
            throw error;
        }
    }

    async createLuckyWheelSpinConfig(configData) {
        try {
            const response = await axios.post(`${this.baseurl}/admin/lucky-wheel-configs`, {
                ...configData,
                brandCode: this.selectedBrandCode()
            });
            return response.data;
        } catch (error) {
            console.error("Error creating lucky wheel spin config:", error);
            throw error;
        }
    }

    async deleteLuckyWheelSpinConfig(configId) {
        try {
            const response = await axios.delete(`${this.baseurl}/admin/lucky-wheel-configs/${configId}`);
            return response.data;
        } catch (error) {
            console.error("Error deleting lucky wheel spin config:", error);
            throw error;
        }
    }

    async createLuckyWheelSpinAddOn(packageId, addOnData) {
        try {
            const response = await axios.post(
                `${this.baseurl}/admin/store-packages/${packageId}/lucky-wheel-spin-add-ons`,
                addOnData
            );
            return response.data;
        } catch (error) {
            console.error("Error creating lucky wheel spin add-on:", error);
            throw error;
        }
    }

    async deleteLuckyWheelSpinAddOn(addOnId) {
        try {
            const response = await axios.delete(`${this.baseurl}/admin/store-lucky-wheel-spin-add-ons/${addOnId}`);
            return response.data;
        } catch (error) {
            console.error("Error deleting lucky wheel spin add-on:", error);
            throw error;
        }
    }

    // Promo Credit Template Add-On Methods

    async createPromoCreditTemplateAddOn(packageId, addOnData) {
        try {
            const response = await axios.post(
                `${this.baseurl}/admin/store-packages/${packageId}/promo-credit-template-add-ons`,
                addOnData
            );
            return response.data;
        } catch (error) {
            console.error("Error creating promo credit template add-on:", error);
            throw error;
        }
    }

    async deletePromoCreditTemplateAddOn(addOnId) {
        try {
            const response = await axios.delete(`${this.baseurl}/admin/store-promo-credit-template-add-ons/${addOnId}`);
            return response.data;
        } catch (error) {
            console.error("Error deleting promo credit template add-on:", error);
            throw error;
        }
    }

    // Bonus Credit Pack Add-On Methods

    async fetchAmwestCreditPacks() {
        try {
            const response = await axios.get(`${this.baseurl}/admin/amwest-credit-packs`);
            return response.data;
        } catch (error) {
            console.error("Failed to fetch AmWest credit packs:", error);
            throw new Error(error?.response?.data?.message || "Failed to fetch AmWest credit packs");
        }
    }

    async getAmwestCreditPack(id) {
        try {
            const response = await axios.get(`${this.baseurl}/admin/amwest-credit-packs/${id}`);
            return response.data;
        } catch (error) {
            console.error("Failed to fetch AmWest credit pack:", error);
            throw new Error(error?.response?.data?.message || "Failed to fetch AmWest credit pack");
        }
    }

    async syncAmwestCreditPacks() {
        try {
            const response = await axios.post(`${this.baseurl}/admin/amwest-credit-packs/sync`);
            return response.data;
        } catch (error) {
            console.error("Failed to sync AmWest credit packs:", error);
            throw new Error(error?.response?.data?.message || "Failed to sync AmWest credit packs");
        }
    }

    async updateAmwestCreditPackMetadata(id, data) {
        try {
            const response = await axios.put(`${this.baseurl}/admin/amwest-credit-packs/${id}/metadata`, data);
            return response.data;
        } catch (error) {
            console.error("Failed to update AmWest credit pack metadata:", error);
            throw new Error(error?.response?.data?.message || "Failed to update AmWest credit pack metadata");
        }
    }

    async createBonusCreditPackAddOn(packageId, addOnData) {
        try {
            const response = await axios.post(
                `${this.baseurl}/admin/store-packages/${packageId}/bonus-credit-pack-add-ons`,
                addOnData
            );
            return response.data;
        } catch (error) {
            console.error("Error creating bonus credit pack add-on:", error);
            throw error;
        }
    }

    async deleteBonusCreditPackAddOn(addOnId) {
        try {
            const response = await axios.delete(`${this.baseurl}/admin/store-bonus-credit-pack-add-ons/${addOnId}`);
            return response.data;
        } catch (error) {
            console.error("Error deleting bonus credit pack add-on:", error);
            throw error;
        }
    }

    // Purchase Offers Methods

    async fetchPurchaseOffers() {
        const response = await axios.get(`${this.baseurl}/admin/purchase-offers`);
        if (!response.data || !response.data.offers) {
            return [];
        }

        return response.data.offers.map(
            offer =>
                new PurchaseOffer(
                    offer.id,
                    offer.brand_id,
                    offer.name,
                    offer.description,
                    offer.price,
                    offer.value,
                    offer.presale_price,
                    offer.icon_url,
                    offer.is_active,
                    offer.is_purchase_offer,
                    offer.is_repurchase_offer,
                    offer.external_id,
                    offer.entitlements,
                    offer.add_ons,
                    offer.free_spin_add_ons,
                    offer.lucky_wheel_add_ons,
                    offer.promo_credit_template_add_ons,
                    offer.bonus_credit_pack_add_ons,
                    offer.tags,
                    offer.start_date,
                    offer.end_date,
                    offer.recurring_purchase_count
                )
        );
    }

    async fetchPurchaseOfferById(offerId) {
        const response = await axios.get(`${this.baseurl}/admin/purchase-offers/${offerId}`);
        const offer = response.data;

        return new PurchaseOffer(
            offer.id,
            offer.brand_id,
            offer.name,
            offer.description,
            offer.price,
            offer.value,
            offer.presale_price,
            offer.icon_url,
            offer.is_active,
            offer.is_purchase_offer,
            offer.is_repurchase_offer,
            offer.external_id,
            offer.entitlements,
            offer.add_ons,
            offer.free_spin_add_ons,
            offer.lucky_wheel_add_ons,
            offer.promo_credit_template_add_ons,
            offer.bonus_credit_pack_add_ons,
            offer.tags,
            offer.start_date,
            offer.end_date,
            offer.recurring_purchase_count
        );
    }

    async updatePurchaseOffer(offerId, offerData) {
        const response = await axios.put(`${this.baseurl}/admin/purchase-offers/${offerId}`, offerData);
        return response.data;
    }

    async createPurchaseOffer(offerData) {
        const response = await axios.post(`${this.baseurl}/admin/purchase-offers`, offerData);
        return response.data;
    }

    async deletePurchaseOffer(offerId) {
        const response = await axios.delete(`${this.baseurl}/admin/purchase-offers/${offerId}`);
        return response.data;
    }

    // Loyalty Tier Management Methods
    async getLoyaltyTiers() {
        try {
            const response = await axios.get(`${this.baseurl}/admin/loyalty-tiers`);
            return response.data;
        } catch (error) {
            console.error("Failed to fetch loyalty tiers:", error);
            throw new Error(error?.response?.data?.message || "Failed to fetch loyalty tiers");
        }
    }

    async getLoyaltyTier(id) {
        try {
            const response = await axios.get(`${this.baseurl}/admin/loyalty-tiers/${id}`);
            return response.data;
        } catch (error) {
            console.error("Failed to fetch loyalty tier:", error);
            throw new Error(error?.response?.data?.message || "Failed to fetch loyalty tier");
        }
    }

    async createLoyaltyTier(tierData) {
        try {
            const response = await axios.post(`${this.baseurl}/admin/loyalty-tiers`, tierData);
            return response.data;
        } catch (error) {
            console.error("Failed to create loyalty tier:", error);
            throw new Error(error?.response?.data?.message || "Failed to create loyalty tier");
        }
    }

    async updateLoyaltyTier(id, tierData) {
        try {
            const response = await axios.put(`${this.baseurl}/admin/loyalty-tiers/${id}`, tierData);
            return response.data;
        } catch (error) {
            console.error("Failed to update loyalty tier:", error);
            throw new Error(error?.response?.data?.message || "Failed to update loyalty tier");
        }
    }

    async deleteLoyaltyTier(id) {
        try {
            const response = await axios.delete(`${this.baseurl}/admin/loyalty-tiers/${id}`);
            return response.data;
        } catch (error) {
            console.error("Failed to delete loyalty tier:", error);
            throw new Error(error?.response?.data?.message || "Failed to delete loyalty tier");
        }
    }

    // Legacy methods for backward compatibility
    async fetchLoyaltyTiers() {
        return this.getLoyaltyTiers();
    }

    async fetchLoyaltyTierById(tierId) {
        return this.getLoyaltyTier(tierId);
    }

    // Rewards Management Methods
    async createRewards(rewardData) {
        try {
            const response = await axios.post(`${this.baseurl}/admin/rewards`, rewardData);
            return response.data;
        } catch (error) {
            console.error("Failed to create rewards:", error);
            throw new Error(error?.response?.data?.message || "Failed to create rewards");
        }
    }

    async deleteRewards(type, attachmentId) {
        try {
            const response = await axios.delete(`${this.baseurl}/admin/rewards`, {
                data: {
                    type: type,
                    attachment_id: attachmentId
                }
            });
            return response.data;
        } catch (error) {
            console.error("Failed to delete rewards:", error);
            throw new Error(error?.response?.data?.message || "Failed to delete rewards");
        }
    }

    async detachReward(ownerType, ownerId, rewardId) {
        try {
            const response = await axios.delete(`${this.baseurl}/admin/rewards/${rewardId}`, {
                data: {
                    owner_type: ownerType,
                    owner_id: ownerId
                }
            });
            return response.data;
        } catch (error) {
            console.error("Failed to detach reward:", error);
            throw new Error(error?.response?.data?.message || "Failed to detach reward");
        }
    }

    // Helper methods for loyalty reward configurations
    async fetchCurrencies() {
        // Mock data matching actual wallets.currency table IDs
        return [
            { code: "real-money", name: "Cash" },
            { code: "loyalty-points", name: "Loyalty Points" }
        ];
    }

    // Quest Management Methods

    async fetchQuestDifficultyTypes() {
        try {
            const response = await axios.get(`${this.baseurl}/admin/quest-difficulty-types`);
            return response.data?.difficulty_types || [];
        } catch (error) {
            console.error("Failed to fetch quest difficulty types:", error);
            throw new Error(error?.response?.data?.message || "Failed to fetch quest difficulty types");
        }
    }

    async fetchQuests() {
        try {
            const response = await axios.get(`${this.baseurl}/admin/quests`);
            return response.data?.quests || [];
        } catch (error) {
            console.error("Failed to fetch quests:", error);
            throw new Error(error?.response?.data?.message || "Failed to fetch quests");
        }
    }

    async fetchQuestById(questId) {
        try {
            const response = await axios.get(`${this.baseurl}/admin/quests/${questId}`);
            return response.data;
        } catch (error) {
            console.error("Failed to fetch quest:", error);
            throw new Error(error?.response?.data?.message || "Failed to fetch quest");
        }
    }

    async createQuest(questData) {
        try {
            const response = await axios.post(`${this.baseurl}/admin/quests`, {
                ...questData,
                brandCode: this.selectedBrandCode()
            });
            return response.data;
        } catch (error) {
            console.error("Failed to create quest:", error);
            throw new Error(error?.response?.data?.message || "Failed to create quest");
        }
    }

    async updateQuest(questId, questData) {
        try {
            const response = await axios.put(`${this.baseurl}/admin/quests/${questId}`, questData);
            return response.data;
        } catch (error) {
            console.error("Failed to update quest:", error);
            throw new Error(error?.response?.data?.message || "Failed to update quest");
        }
    }

    async deleteQuest(questId) {
        try {
            const response = await axios.delete(`${this.baseurl}/admin/quests/${questId}`);
            return response.data;
        } catch (error) {
            console.error("Failed to delete quest:", error);
            throw new Error(error?.response?.data?.message || "Failed to delete quest");
        }
    }

    // Quest Step Management Methods
    async createQuestStep(stepData) {
        try {
            const questId = stepData.quest_id;
            const response = await axios.post(`${this.baseurl}/admin/quests/${questId}/steps`, stepData);
            return response.data;
        } catch (error) {
            console.error("Failed to create quest step:", error);
            throw new Error(error?.response?.data?.message || "Failed to create quest step");
        }
    }

    async updateQuestStep(stepId, stepData) {
        try {
            const response = await axios.put(`${this.baseurl}/admin/quest-steps/${stepId}`, stepData);
            return response.data;
        } catch (error) {
            console.error("Failed to update quest step:", error);
            throw new Error(error?.response?.data?.message || "Failed to update quest step");
        }
    }

    async deleteQuestStep(stepId) {
        try {
            const response = await axios.delete(`${this.baseurl}/admin/quest-steps/${stepId}`);
            return response.data;
        } catch (error) {
            console.error("Failed to delete quest step:", error);
            throw new Error(error?.response?.data?.message || "Failed to delete quest step");
        }
    }

    async reorderQuestSteps(reorderData) {
        try {
            const response = await axios.put(`${this.baseurl}/admin/quest-steps/reorder`, reorderData);
            return response.data;
        } catch (error) {
            console.error("Failed to reorder quest steps:", error);
            throw new Error(error?.response?.data?.message || "Failed to reorder quest steps");
        }
    }

    // Quest Schedule Management Methods
    async fetchQuestSchedules() {
        try {
            const response = await axios.get(`${this.baseurl}/admin/quest-schedules`);
            return response.data?.schedules || [];
        } catch (error) {
            console.error("Failed to fetch quest schedules:", error);
            throw new Error(error?.response?.data?.message || "Failed to fetch quest schedules");
        }
    }

    async fetchQuestScheduleById(scheduleId) {
        try {
            const response = await axios.get(`${this.baseurl}/admin/quest-schedules/${scheduleId}`);
            return response.data?.schedule;
        } catch (error) {
            console.error("Failed to fetch quest schedule:", error);
            throw new Error(error?.response?.data?.message || "Failed to fetch quest schedule");
        }
    }

    async createQuestSchedule(scheduleData) {
        try {
            const response = await axios.post(`${this.baseurl}/admin/quest-schedules`, {
                ...scheduleData,
                brandCode: this.selectedBrandCode()
            });
            return response.data?.schedule;
        } catch (error) {
            console.error("Failed to create quest schedule:", error);
            throw new Error(error?.response?.data?.message || "Failed to create quest schedule");
        }
    }

    async updateQuestSchedule(scheduleId, scheduleData) {
        try {
            const response = await axios.put(`${this.baseurl}/admin/quest-schedules/${scheduleId}`, scheduleData);
            return response.data;
        } catch (error) {
            console.error("Failed to update quest schedule:", error);
            throw new Error(error?.response?.data?.message || "Failed to update quest schedule");
        }
    }

    async deleteQuestSchedule(scheduleId) {
        try {
            await axios.delete(`${this.baseurl}/admin/quest-schedules/${scheduleId}`);
        } catch (error) {
            console.error("Failed to delete quest schedule:", error);
            throw new Error(error?.response?.data?.message || "Failed to delete quest schedule");
        }
    }

    async createQuestScheduleEntry(scheduleId, entryData) {
        try {
            const requestBody = {
                schedule_id: scheduleId,
                quest_id: entryData.quest_id?.value || entryData.quest_id,
                dayOfWeek: entryData.dayOfWeek?.value || entryData.dayOfWeek
            };
            const response = await axios.post(
                `${this.baseurl}/admin/quest-schedules/${scheduleId}/entries`,
                requestBody
            );
            return response.data?.entry;
        } catch (error) {
            console.error("Failed to create quest schedule entry:", error);
            throw new Error(error?.response?.data?.message || "Failed to create quest schedule entry");
        }
    }

    async deleteQuestScheduleEntry(scheduleId, entryId) {
        try {
            await axios.delete(`${this.baseurl}/admin/quest-schedules/${scheduleId}/entries/${entryId}`);
        } catch (error) {
            console.error("Failed to delete quest schedule entry:", error);
            throw new Error(error?.response?.data?.message || "Failed to delete quest schedule entry");
        }
    }

    // Promotion Management Methods
    async fetchPromotions() {
        try {
            const response = await axios.get(`${this.baseurl}/admin/promotions`);
            return response.data?.promotions || [];
        } catch (error) {
            console.error("Failed to fetch promotions:", error);
            throw new Error(error?.response?.data?.message || "Failed to fetch promotions");
        }
    }

    /**
     * Fetch promotions for a specific user with their progress
     * @param {number} brandUserId - The user's brand_user_id
     * @returns {Promise<Array>} - Array of user promotions with progress data
     */
    async fetchUserPromotions(brandUserId, { limit = 100, page = 1, status } = {}) {
        try {
            const params = { limit, page };
            if (status) params.status = status;
            const response = await axios.get(`${this.baseurl}/admin/users/${brandUserId}/promotions`, {
                params
            });
            return response.data;
        } catch (error) {
            console.error("Failed to fetch user promotions:", error);
            // Return empty object on error instead of throwing
            return { promotions: [], total: 0, summary: {} };
        }
    }

    async fetchPromotionById(promotionId) {
        try {
            const response = await axios.get(`${this.baseurl}/admin/promotions/${promotionId}`);
            return response.data;
        } catch (error) {
            console.error("Failed to fetch promotion:", error);
            throw new Error(error?.response?.data?.message || "Failed to fetch promotion");
        }
    }

    async createPromotion(promotionData) {
        try {
            const response = await axios.post(`${this.baseurl}/admin/promotions`, promotionData);
            return response.data;
        } catch (error) {
            console.error("Failed to create promotion:", error);
            throw new Error(error?.response?.data?.message || "Failed to create promotion");
        }
    }

    async updatePromotion(promotionId, promotionData) {
        try {
            const response = await axios.put(`${this.baseurl}/admin/promotions/${promotionId}`, promotionData);
            return response.data;
        } catch (error) {
            console.error("Failed to update promotion:", error);
            throw new Error(error?.response?.data?.message || "Failed to update promotion");
        }
    }

    async deletePromotion(promotionId) {
        try {
            const response = await axios.delete(`${this.baseurl}/admin/promotions/${promotionId}`);
            return response.data;
        } catch (error) {
            console.error("Failed to delete promotion:", error);
            throw new Error(error?.response?.data?.message || "Failed to delete promotion");
        }
    }

    // Task Management Methods
    async fetchTaskCurrencies() {
        try {
            const response = await axios.get(`${this.baseurl}/admin/risk/lookup/currencies`);
            // Map the response to the expected format { id, code, name }
            return (response.data || []).map(c => ({
                id: c.currency_mode_id,
                code: c.currency_code,
                name: c.display_name
            }));
        } catch (error) {
            console.error("Failed to fetch task currencies:", error);
            throw new Error(error?.response?.data?.message || "Failed to fetch currencies");
        }
    }

    async createTask(taskData) {
        try {
            const response = await axios.post(`${this.baseurl}/admin/tasks`, taskData);
            return response.data;
        } catch (error) {
            console.error("Failed to create task:", error);
            throw new Error(error?.response?.data?.message || "Failed to create task");
        }
    }

    async updateTask(taskId, data) {
        try {
            const response = await axios.put(`${this.baseurl}/admin/tasks/${taskId}`, data);
            return response.data;
        } catch (error) {
            console.error("Failed to update task:", error);
            throw new Error(error?.response?.data?.message || "Failed to update task");
        }
    }

    async deleteTask(taskId) {
        try {
            const response = await axios.delete(`${this.baseurl}/admin/tasks/${taskId}`);
            return response.data;
        } catch (error) {
            console.error("Failed to delete task:", error);
            throw new Error(error?.response?.data?.message || "Failed to delete task");
        }
    }

    async detachTaskFromPromotion(promotionId, taskId) {
        try {
            const response = await axios.delete(`${this.baseurl}/admin/promotions/${promotionId}/tasks/${taskId}`);
            return response.data;
        } catch (error) {
            console.error("Failed to detach task from promotion:", error);
            throw new Error(error?.response?.data?.message || "Failed to detach task from promotion");
        }
    }

    // Advent Calendar Management Methods
    async fetchAdventCalendars() {
        try {
            const response = await axios.get(`${this.baseurl}/admin/advent-calendars`);
            return response.data || [];
        } catch (error) {
            console.error("Failed to fetch advent calendars:", error);
            throw new Error(error?.response?.data?.message || "Failed to fetch advent calendars");
        }
    }

    // Methods moved below to avoid duplication

    // Advent Calendar Management
    async fetchAdventCalendars() {
        try {
            const response = await axios.get(`${this.baseurl}/admin/advent-calendars`);
            return response.data?.calendars || [];
        } catch (error) {
            console.error("Failed to fetch advent calendars:", error);
            throw new Error(error?.response?.data?.message || "Failed to fetch advent calendars");
        }
    }

    async fetchAdventCalendar(calendarId) {
        try {
            const response = await axios.get(`${this.baseurl}/admin/advent-calendars/${calendarId}`);
            return response.data?.calendar || response.data;
        } catch (error) {
            console.error("Failed to fetch advent calendar:", error);
            throw new Error(error?.response?.data?.message || "Failed to fetch advent calendar");
        }
    }

    async createAdventCalendar(data) {
        try {
            // Add brandCode to request
            const requestData = {
                ...data,
                brand_code: this.selectedBrandCode()
            };
            const response = await axios.post(`${this.baseurl}/admin/advent-calendars`, requestData);
            return response.data;
        } catch (error) {
            console.error("Failed to create advent calendar:", error);
            throw new Error(error?.response?.data?.message || "Failed to create advent calendar");
        }
    }

    async updateAdventCalendar(calendarId, data) {
        try {
            // brand_id is handled by backend
            const response = await axios.put(`${this.baseurl}/admin/advent-calendars/${calendarId}`, data);
            return response.data;
        } catch (error) {
            console.error("Failed to update advent calendar:", error);
            throw new Error(error?.response?.data?.message || "Failed to update advent calendar");
        }
    }

    async deleteAdventCalendar(calendarId) {
        try {
            const response = await axios.delete(`${this.baseurl}/admin/advent-calendars/${calendarId}`);
            return response.data;
        } catch (error) {
            console.error("Failed to delete advent calendar:", error);
            throw new Error(error?.response?.data?.message || "Failed to delete advent calendar");
        }
    }

    // Daily Bonus Management (Entries)
    async fetchDailyBonusEntries(calendarId) {
        const response = await axios.get(`${this.baseurl}/admin/daily-bonus`, {
            params: { advent_calendar_id: calendarId }
        });
        return response.data?.entries || [];
    }

    async fetchDailyBonusEntry(entryId) {
        const response = await axios.get(`${this.baseurl}/admin/daily-bonus/${entryId}`);
        return response.data;
    }

    async createDailyBonusEntry(data) {
        // Ensure calendar_id is included and add brandCode
        const requestData = {
            ...data,
            advent_calendar_id: data.advent_calendar_id || data.calendar_id,
            brand_code: this.selectedBrandCode()
        };
        const response = await axios.post(`${this.baseurl}/admin/daily-bonus`, requestData);
        return response.data;
    }

    async updateDailyBonusEntry(entryId, data) {
        // brand_id is handled by backend
        await axios.put(`${this.baseurl}/admin/daily-bonus/${entryId}`, data);
    }

    async deleteDailyBonusEntry(entryId) {
        await axios.delete(`${this.baseurl}/admin/daily-bonus/${entryId}`);
    }

    // Edits one reward attached to a daily bonus day (#2439). rewardId is the
    // association id, the same id detachReward takes. Currency edits are
    // copy-on-write on the backend, so the shared reward pool is never mutated.
    async updateDailyBonusReward(rewardId, data) {
        // brand_id is handled by backend
        await axios.put(`${this.baseurl}/admin/daily-bonus/rewards/${rewardId}`, data);
    }

    async reorderDailyBonusEntry(entryId, displayOrder) {
        // brand_id is handled by backend
        const requestData = {
            entry_id: parseInt(entryId),
            display_order: parseInt(displayOrder)
        };

        await axios.put(`${this.baseurl}/admin/daily-bonus/reorder`, requestData);
    }

    async reorderDailyBonusEntriesBulk(entryOrders) {
        // OPTIMIZED: Use parallel processing for maximum speed
        return this.reorderDailyBonusEntriesBulkOptimized(entryOrders);
    }

    async reorderDailyBonusEntriesBulkOptimized(entryOrders) {
        // OPTIMIZED: Use parallel processing for maximum speed
        const results = [];

        // Step 1: Move all entries to temporary positions in parallel (much faster)
        const tempPromises = entryOrders.map(async (order, i) => {
            try {
                const tempOrder = 10000 + i;
                await this.reorderDailyBonusEntry(order.entryId, tempOrder);
                return { success: true, entryId: order.entryId, step: "temp" };
            } catch (error) {
                return { success: false, entryId: order.entryId, error, step: "temp" };
            }
        });

        // Wait for all temporary moves to complete
        const tempResults = await Promise.all(tempPromises);
        results.push(...tempResults);

        // Check if any temporary moves failed
        const tempFailures = tempResults.filter(r => !r.success);
        if (tempFailures.length > 0) {
            throw new Error(`Failed to move ${tempFailures.length} entries to temporary positions`);
        }

        // Step 2: Move all entries to their final positions in parallel
        const finalPromises = entryOrders.map(async order => {
            try {
                await this.reorderDailyBonusEntry(order.entryId, order.displayOrder);
                return { success: true, entryId: order.entryId, step: "final" };
            } catch (error) {
                return { success: false, entryId: order.entryId, error, step: "final" };
            }
        });

        // Wait for all final moves to complete
        const finalResults = await Promise.all(finalPromises);
        results.push(...finalResults);

        return results;
    }

    async fetchDailyBonusRewards(entryId) {
        const response = await axios.get(`${this.baseurl}/admin/daily-bonus/${entryId}/rewards`);
        return response.data?.rewards || [];
    }

    // Admin User Management Methods
    async fetchAdminUsers() {
        try {
            const response = await axios.get(`${this.baseurl}/admin/list-admin-users`);
            return response.data.users || [];
        } catch (error) {
            console.error("Failed to fetch admin users:", error);
            throw new Error(error?.response?.data?.message || "Failed to fetch admin users");
        }
    }

    async createAdminUser(userData) {
        try {
            const response = await axios.post(`${this.baseurl}/admin/manage-user`, {
                username: userData.username,
                password: userData.password,
                activate: true
            });
            return response.data;
        } catch (error) {
            console.error("Failed to create admin user:", error);
            throw new Error(error?.response?.data?.message || "Failed to create admin user");
        }
    }

    async deactivateAdminUser(username) {
        try {
            const response = await axios.post(`${this.baseurl}/admin/manage-user`, {
                username: username,
                activate: false
            });
            return response.data;
        } catch (error) {
            console.error("Failed to deactivate admin user:", error);
            throw new Error(error?.response?.data?.message || "Failed to deactivate admin user");
        }
    }

    // ===== POPUP MANAGEMENT =====

    // Popup Queues
    async getPopupQueues() {
        try {
            const response = await axios.get(`${this.baseurl}/admin/popup-queues`);
            return response.data;
        } catch (error) {
            console.error("Failed to fetch popup queues:", error);
            throw new Error(error?.response?.data?.message || "Failed to fetch popup queues");
        }
    }

    async getPopupQueue(queueId) {
        try {
            const response = await axios.get(`${this.baseurl}/admin/popup-queues/${queueId}`);
            return response.data;
        } catch (error) {
            console.error("Failed to fetch popup queue:", error);
            throw new Error(error?.response?.data?.message || "Failed to fetch popup queue");
        }
    }

    async createPopupQueue(queueData) {
        try {
            const response = await axios.post(`${this.baseurl}/admin/popup-queues`, {
                ...queueData,
                brandCode: this.selectedBrandCode()
            });
            return response.data;
        } catch (error) {
            console.error("Failed to create popup queue:", error);
            throw new Error(error?.response?.data?.message || "Failed to create popup queue");
        }
    }

    async updatePopupQueue(queueId, queueData) {
        try {
            const response = await axios.put(`${this.baseurl}/admin/popup-queues/${queueId}`, queueData);
            return response.data;
        } catch (error) {
            console.error("Failed to update popup queue:", error);
            throw new Error(error?.response?.data?.message || "Failed to update popup queue");
        }
    }

    async deletePopupQueue(queueId) {
        try {
            const response = await axios.delete(`${this.baseurl}/admin/popup-queues/${queueId}`);
            return response.data;
        } catch (error) {
            console.error("Failed to delete popup queue:", error);
            throw new Error(error?.response?.data?.message || "Failed to delete popup queue");
        }
    }

    // Popup Entries
    async getPopupEntriesForQueue(queueId) {
        try {
            const response = await axios.get(`${this.baseurl}/admin/popup-queues/${queueId}/entries`);
            return response.data;
        } catch (error) {
            console.error("Failed to fetch popup entries:", error);
            throw new Error(error?.response?.data?.message || "Failed to fetch popup entries");
        }
    }

    async getPopupEntry(entryId) {
        try {
            const response = await axios.get(`${this.baseurl}/admin/popup-entries/${entryId}`);
            return response.data;
        } catch (error) {
            console.error("Failed to fetch popup entry:", error);
            throw new Error(error?.response?.data?.message || "Failed to fetch popup entry");
        }
    }

    async createPopupEntry(entryData) {
        try {
            const response = await axios.post(`${this.baseurl}/admin/popup-entries`, entryData);
            return response.data;
        } catch (error) {
            console.error("Failed to create popup entry:", error);
            throw new Error(error?.response?.data?.message || "Failed to create popup entry");
        }
    }

    async updatePopupEntry(entryId, entryData) {
        try {
            const response = await axios.put(`${this.baseurl}/admin/popup-entries/${entryId}`, entryData);
            return response.data;
        } catch (error) {
            console.error("Failed to update popup entry:", error);
            throw new Error(error?.response?.data?.message || "Failed to update popup entry");
        }
    }

    async deletePopupEntry(entryId) {
        try {
            const response = await axios.delete(`${this.baseurl}/admin/popup-entries/${entryId}`);
            return response.data;
        } catch (error) {
            console.error("Failed to delete popup entry:", error);
            throw new Error(error?.response?.data?.message || "Failed to delete popup entry");
        }
    }

    // AMOE Queue Methods
    async fetchAMOEQueue(page = 1, limit = 20) {
        try {
            const response = await axios.get(`${this.baseurl}/admin/amoe-queue`, {
                params: { page, limit }
            });
            return response.data;
        } catch (error) {
            console.error("Failed to fetch AMOE queue:", error);
            throw new Error(error?.response?.data?.message || "Failed to fetch AMOE queue");
        }
    }

    async searchAMOEQueue(filters = {}) {
        try {
            const response = await axios.get(`${this.baseurl}/admin/amoe-queue/search`, {
                params: filters
            });
            return response.data;
        } catch (error) {
            console.error("Failed to search AMOE queue:", error);
            throw new Error(error?.response?.data?.message || "Failed to search AMOE queue");
        }
    }

    async updateAMOESubmissionReview(submissionId, validity, codeId, newStatus) {
        try {
            // Reviewer identity is derived server-side from the admin session.
            const response = await axios.post(`${this.baseurl}/admin/amoe-queue/submission/review`, {
                submission_id: submissionId,
                validity,
                code_id: codeId,
                new_status: newStatus
            });
            return response.data;
        } catch (error) {
            console.error("Failed to update AMOE submission review:", error);
            throw new Error(error?.response?.data?.message || "Failed to update AMOE submission review");
        }
    }

    async fetchUserAMOECodes(brandUserId, filters = {}) {
        try {
            const response = await axios.get(`${this.baseurl}/admin/amoe-queue/search`, {
                params: {
                    brand_user_id: brandUserId,
                    ...filters
                }
            });
            return response.data;
        } catch (error) {
            console.error("Failed to fetch user AMOE codes:", error);
            throw new Error(error?.response?.data?.message || "Failed to fetch user AMOE codes");
        }
    }

    // ===== AFFILIATE MANAGEMENT =====

    async getAffiliates(filters = {}) {
        try {
            const params = new URLSearchParams();

            // Add pagination
            if (filters.page && filters.limit) {
                const offset = (filters.page - 1) * filters.limit;
                params.append("offset", offset);
            }
            if (filters.limit) params.append("limit", filters.limit);

            // Add sorting
            if (filters.sort_field) params.append("sort_field", filters.sort_field);
            if (filters.sort_order) {
                // Convert numeric sort order to string
                const sortOrder = filters.sort_order === -1 ? "desc" : "asc";
                params.append("sort_order", sortOrder);
            }

            // Add search filters
            if (filters.email) params.append("email", filters.email);
            if (filters.first_name) params.append("first_name", filters.first_name);
            if (filters.last_name) params.append("last_name", filters.last_name);
            if (filters.name) params.append("name", filters.name);
            if (filters.hierarchy_level) params.append("hierarchy_level", filters.hierarchy_level);

            // Add date filters
            if (filters.created_at_from) params.append("created_at_from", filters.created_at_from);
            if (filters.created_at_to) params.append("created_at_to", filters.created_at_to);

            const url = `${this.baseurl}/admin/affiliates/search?${params.toString()}`;
            const response = await axios.get(url);
            return response.data;
        } catch (error) {
            throw error;
        }
    }

    async getAffiliate(id) {
        try {
            const url = `${this.baseurl}/admin/affiliates/${id}`;
            const response = await axios.get(url);
            return response.data;
        } catch (error) {
            throw error;
        }
    }

    async createAffiliate(affiliateData) {
        try {
            const response = await axios.post(`${this.baseurl}/admin/affiliates`, {
                ...affiliateData,
                brandCode: this.selectedBrandCode()
            });
            return response.data;
        } catch (error) {
            throw error;
        }
    }

    async getBonusCodes(filters = {}) {
        try {
            const params = new URLSearchParams();

            // Add pagination
            if (filters.offset !== undefined) params.append("offset", filters.offset);
            if (filters.limit) params.append("limit", filters.limit);

            // Add search filters
            if (filters.code) params.append("code", filters.code);
            if (filters.affiliate_user_id) params.append("affiliate_user_id", filters.affiliate_user_id);
            if (filters.affiliate_name) params.append("affiliate_name", filters.affiliate_name);

            // Add date filters
            if (filters.created_at_from) params.append("created_at_from", filters.created_at_from);
            if (filters.created_at_to) params.append("created_at_to", filters.created_at_to);

            // Add sorting
            if (filters.sort_field) params.append("sort_field", filters.sort_field);
            if (filters.sort_order) params.append("sort_order", filters.sort_order);

            // Add stats date range filters
            if (filters.stats_date_from) params.append("stats_date_from", filters.stats_date_from);
            if (filters.stats_date_to) params.append("stats_date_to", filters.stats_date_to);

            const url = `${this.baseurl}/admin/bonus-codes/search?${params.toString()}`;
            const response = await axios.get(url);
            return response.data;
        } catch (error) {
            throw error;
        }
    }

    async updateAffiliate(id, data) {
        try {
            const response = await axios.put(`${this.baseurl}/admin/affiliates/${id}`, data);
            return response.data;
        } catch (error) {
            throw error;
        }
    }

    async deleteAffiliate(id) {
        try {
            const response = await axios.delete(`${this.baseurl}/admin/affiliates/${id}`);
            return response.data;
        } catch (error) {
            throw error;
        }
    }

    async searchAffiliates(filters) {
        return this.getAffiliates(filters);
    }

    async createBonusCode(data) {
        try {
            const response = await axios.post(`${this.baseurl}/admin/bonus-codes`, data);
            return response.data;
        } catch (error) {
            throw error;
        }
    }

    async updateBonusCode(id, data) {
        try {
            const response = await axios.put(`${this.baseurl}/admin/bonus-codes/${id}`, data);
            return response.data;
        } catch (error) {
            throw error;
        }
    }

    async deleteBonusCode(id) {
        try {
            const response = await axios.delete(`${this.baseurl}/admin/bonus-codes/${id}`);
            return response.data;
        } catch (error) {
            throw error;
        }
    }

    async getBonusCodeRegistrations(bonusCodeId) {
        try {
            const response = await axios.get(`${this.baseurl}/admin/bonus-codes/${bonusCodeId}/registrations`);
            return response.data;
        } catch (error) {
            throw new Error(error?.response?.data?.message || "Failed to fetch bonus code registrations");
        }
    }

    async exportBonusCodeStatsCSV(searchParams = {}) {
        try {
            const params = {};
            if (searchParams.affiliate_user_id) params.affiliate_user_id = searchParams.affiliate_user_id;
            if (searchParams.code) params.code = searchParams.code;
            if (searchParams.created_at_from) params.created_at_from = searchParams.created_at_from;
            if (searchParams.created_at_to) params.created_at_to = searchParams.created_at_to;
            if (searchParams.stats_date_from) params.stats_date_from = searchParams.stats_date_from;
            if (searchParams.stats_date_to) params.stats_date_to = searchParams.stats_date_to;

            const response = await axios.get(`${this.baseurl}/admin/bonus-codes/export`, {
                params,
                responseType: "blob"
            });
            return response.data;
        } catch (error) {
            console.error("Failed to export bonus code stats CSV:", error);
            let message = "Failed to export bonus code stats CSV";
            const data = error?.response?.data;
            if (data instanceof Blob) {
                try {
                    const parsed = JSON.parse(await data.text());
                    if (parsed?.message) message = parsed.message;
                } catch { /* ignore parse error */ }
            }
            throw new Error(message);
        }
    }

    // Analytics
    async getAffiliateStats(affiliateId) {
        // TODO: Implement real analytics endpoint
        return {
            affiliate_id: parseInt(affiliateId),
            total_referrals: 0,
            total_commission: 0,
            bonus_codes_count: 0,
            active_bonus_codes: 0,
            total_bonus_code_usage: 0,
            last_activity: new Date().toISOString()
        };
    }

    // IP Block Management
    async listIPBlocks() {
        try {
            const response = await axios.get(`${this.baseurl}/admin/security/ip-blocklist`);
            return response.data;
        } catch (error) {
            throw error;
        }
    }

    async createIPBlock(data) {
        try {
            const response = await axios.post(`${this.baseurl}/admin/security/ip-blocklist`, data);
            return response.data;
        } catch (error) {
            throw error;
        }
    }

    async deleteIPBlock(id) {
        try {
            const response = await axios.delete(`${this.baseurl}/admin/security/ip-blocklist/${id}`);
            return response.data;
        } catch (error) {
            throw error;
        }
    }

    async updateIPBlock(data) {
        try {
            const response = await axios.put(`${this.baseurl}/admin/security/ip-blocklist/${data.id}`, data);
            return response.data;
        } catch (error) {
            throw error;
        }
    }

    // Banned Domains Management
    async listBannedDomains() {
        try {
            const response = await axios.get(`${this.baseurl}/admin/banned-domains`);
            return response.data;
        } catch (error) {
            throw error;
        }
    }

    async createBannedDomain(domain) {
        try {
            const response = await axios.post(`${this.baseurl}/admin/banned-domains`, { domain });
            return response.data;
        } catch (error) {
            throw error;
        }
    }

    async deleteBannedDomain(id) {
        try {
            const response = await axios.delete(`${this.baseurl}/admin/banned-domains/${id}`);
            return response.data;
        } catch (error) {
            throw error;
        }
    }

    async getGlobalConfigurations() {
        try {
            const response = await axios.get(`${this.baseurl}/admin/global-configurations`);
            return response.data;
        } catch (error) {
            console.error("Failed to get global configurations:", error);
            return { configurations: {} };
        }
    }

    async updateGlobalConfigurations(configurations) {
        try {
            const response = await axios.put(`${this.baseurl}/admin/global-configurations`, configurations);
            return response.data;
        } catch (error) {
            console.error("Failed to update global configurations:", error);
            throw new Error(error?.response?.data?.message || "Failed to update global configurations");
        }
    }

    // ===== REPURCHASE CATALOG =====

    async getRepurchaseCatalog() {
        try {
            const response = await axios.get(`${this.baseurl}/admin/repurchase-catalog`);
            return response.data;
        } catch (error) {
            console.error("Failed to get repurchase catalog:", error);
            throw new Error(error?.response?.data?.message || "Failed to get repurchase catalog");
        }
    }

    async getRepurchaseEligibleProducts() {
        try {
            const response = await axios.get(`${this.baseurl}/admin/repurchase-catalog/eligible-products`);
            return response.data;
        } catch (error) {
            console.error("Failed to get repurchase eligible products:", error);
            throw new Error(error?.response?.data?.message || "Failed to get eligible products");
        }
    }

    async addToRepurchaseCatalog(productId, displayOrder = 0) {
        try {
            const response = await axios.post(`${this.baseurl}/admin/repurchase-catalog`, {
                product_id: productId,
                display_order: displayOrder
            });
            return response.data;
        } catch (error) {
            console.error("Failed to add to repurchase catalog:", error);
            throw new Error(error?.response?.data?.message || "Failed to add product to catalog");
        }
    }

    async removeFromRepurchaseCatalog(catalogId) {
        try {
            const response = await axios.delete(`${this.baseurl}/admin/repurchase-catalog/${catalogId}`);
            return response.data;
        } catch (error) {
            console.error("Failed to remove from repurchase catalog:", error);
            throw new Error(error?.response?.data?.message || "Failed to remove product from catalog");
        }
    }

    // ===== AUTO-REBUY CATALOG =====

    async getAutoRebuyCatalog() {
        try {
            const response = await axios.get(`${this.baseurl}/admin/auto-rebuy-catalog`);
            return response.data;
        } catch (error) {
            console.error("Failed to get auto-rebuy catalog:", error);
            throw new Error(error?.response?.data?.message || "Failed to get auto-rebuy catalog");
        }
    }

    async getAutoRebuyEligibleProducts() {
        try {
            const response = await axios.get(`${this.baseurl}/admin/auto-rebuy-catalog/eligible-products`);
            return response.data;
        } catch (error) {
            console.error("Failed to get auto-rebuy eligible products:", error);
            throw new Error(error?.response?.data?.message || "Failed to get eligible products");
        }
    }

    async addToAutoRebuyCatalog(productId, displayOrder = 0) {
        try {
            const response = await axios.post(`${this.baseurl}/admin/auto-rebuy-catalog`, {
                product_id: productId,
                display_order: displayOrder
            });
            return response.data;
        } catch (error) {
            console.error("Failed to add to auto-rebuy catalog:", error);
            throw new Error(error?.response?.data?.message || "Failed to add product to catalog");
        }
    }

    async removeFromAutoRebuyCatalog(catalogId) {
        try {
            const response = await axios.delete(`${this.baseurl}/admin/auto-rebuy-catalog/${catalogId}`);
            return response.data;
        } catch (error) {
            console.error("Failed to remove from auto-rebuy catalog:", error);
            throw new Error(error?.response?.data?.message || "Failed to remove product from catalog");
        }
    }

    // ===== BRAND CONFIGURATION =====

    async getBrandConfig() {
        try {
            const response = await axios.get(`${this.baseurl}/admin/brand-config`);
            return response.data;
        } catch (error) {
            console.error("Failed to get brand config:", error);
            throw new Error(error?.response?.data?.message || "Failed to get brand configuration");
        }
    }

    async updateBrandConfig(config) {
        try {
            const response = await axios.put(`${this.baseurl}/admin/brand-config`, config);
            return response.data;
        } catch (error) {
            console.error("Failed to update brand config:", error);
            throw new Error(error?.response?.data?.message || "Failed to update brand configuration");
        }
    }

    async listBrandConfigs() {
        try {
            const response = await axios.get(`${this.baseurl}/admin/brand-configs`);
            return response.data;
        } catch (error) {
            console.error("Failed to list brand configs:", error);
            throw new Error(error?.response?.data?.message || "Failed to list brand configurations");
        }
    }

    // ===== BRAND SWITCHER (#2122) =====

    // Return the brand the current admin's session is scoped to. Resolved on the
    // backend from the persisted selection, so the frontend can hydrate its
    // switcher (and X-Brand-Code header) to exactly the brand the backend owns.
    async getMySelectedBrand() {
        const response = await axios.get(`${this.baseurl}/admin/me/brand`);
        return response.data;
    }

    // Persist the admin's brand selection. Returns the resolved { id, code, name }.
    async updateSelectedBrand(code) {
        try {
            const response = await axios.put(`${this.baseurl}/admin/me/brand`, { code });
            return response.data;
        } catch (error) {
            console.error("Failed to update selected brand:", error);
            // Preserve the HTTP status so callers can tell a transient failure
            // (network / 5xx, retryable) from a rejected code (4xx, permanent).
            const wrapped = new Error(error?.response?.data?.message || "Failed to update selected brand");
            wrapped.status = error?.response?.status ?? 0;
            throw wrapped;
        }
    }

    // ===== MAINTENANCE MODE =====

    async getMaintenanceMode() {
        try {
            const response = await axios.get(`${this.baseurl}/admin/system/maintenance/status`);
            return response.data;
        } catch (error) {
            console.error("Failed to get maintenance mode status:", error);
            throw new Error(error?.response?.data?.message || "Failed to get maintenance mode status");
        }
    }

    async enableMaintenanceMode(message) {
        try {
            const response = await axios.post(`${this.baseurl}/admin/system/maintenance/enable`, {
                message: message
            });
            return response.data;
        } catch (error) {
            console.error("Failed to enable maintenance mode:", error);
            throw new Error(error?.response?.data?.message || "Failed to enable maintenance mode");
        }
    }

    async disableMaintenanceMode() {
        try {
            const response = await axios.post(`${this.baseurl}/admin/system/maintenance/disable`, {});
            return response.data;
        } catch (error) {
            console.error("Failed to disable maintenance mode:", error);
            throw new Error(error?.response?.data?.message || "Failed to disable maintenance mode");
        }
    }

    async getToteMaintenanceConfig() {
        try {
            const response = await axios.get(`${this.baseurl}/admin/system/tote-maintenance/config`);
            return response.data;
        } catch (error) {
            console.error("Failed to get tote maintenance config:", error);
            throw new Error(error?.response?.data?.message || "Failed to get tote maintenance config");
        }
    }

    async updateToteMaintenanceConfig(config) {
        try {
            const response = await axios.put(`${this.baseurl}/admin/system/tote-maintenance/config`, config);
            return response.data;
        } catch (error) {
            console.error("Failed to update tote maintenance config:", error);
            throw new Error(error?.response?.data?.message || "Failed to update tote maintenance config");
        }
    }

    // ===== ACCOUNT CREDITS =====

    /**
     * Adjust a user's wallet balance. Positive amounts credit, negative debit.
     *
     * Two mutually exclusive amount forms, matching the API:
     *  - `amount`: bucket-agnostic. The server credits to non-redeemable and
     *    debits non-redeemable first, then redeemable. This is the only form
     *    gold coins accept, having no redeemable split (#2438).
     *  - `unconvertedAmount` / `convertedAmount`: explicit, for wallets where
     *    the split is real (sweeps coins, USD). Either may be omitted.
     *
     * @param {Object} params
     * @param {number|string} params.brandUserId
     * @param {string} params.currencyMode - gold_coin | sweeps_coin | cash | usd
     * @param {string} [params.amount] - signed, bucket-agnostic
     * @param {string} [params.unconvertedAmount] - signed, non-redeemable
     * @param {string} [params.convertedAmount] - signed, redeemable
     * @param {string} params.reason - required, persisted to the audit trail
     */
    async adjustAccountCredits({ brandUserId, currencyMode, amount, unconvertedAmount, convertedAmount, reason }) {
        try {
            const payload = { currency_mode: currencyMode, reason: reason };
            if (amount !== undefined) payload.amount = amount;
            if (unconvertedAmount !== undefined) payload.unconverted_amount = unconvertedAmount;
            if (convertedAmount !== undefined) payload.converted_amount = convertedAmount;

            const response = await axios.post(`${this.baseurl}/admin/users/${brandUserId}/credits`, payload);
            return response.data;
        } catch (error) {
            console.error("Failed to adjust account credits:", error);
            throw new Error(error?.response?.data?.message || "Failed to adjust account credits");
        }
    }

    /**
     * Grant RMG promotional credits to a user via the Amwest API
     * @param {number|string} brandUserId - The brand user ID
     * @param {string} credits - Number of promo credits to issue (minimum: 1)
     * @param {string} cashAmount - Optional cash payout amount (defaults to "0")
     * @param {string} fundType - Where funds land: "converted" (redeemable) or "unconverted" (non-redeemable)
     * @param {string} reason - Reason for granting credits (required)
     * @param {string} description - Optional description
     * @param {string} referenceId - Optional idempotency key
     * @returns {Promise<Object>} Response with promo_request_id, new_promo_balance, etc.
     */
    async grantRMGCredits(
        brandUserId,
        credits,
        cashAmount = "0",
        fundType = "unconverted",
        reason = "",
        description = "",
        referenceId = ""
    ) {
        try {
            const payload = {
                credits: credits,
                cash_amount: cashAmount,
                fund_type: fundType,
                reason: reason
            };

            if (description) {
                payload.description = description;
            }

            if (referenceId) {
                payload.reference_id = referenceId;
            }

            const response = await axios.post(`${this.baseurl}/admin/users/${brandUserId}/rmg-credit`, payload);
            return response.data;
        } catch (error) {
            console.error("Failed to grant RMG credits:", error);
            throw new Error(error?.response?.data?.message || "Failed to grant RMG credits");
        }
    }

    /**
     * Grant RMG promotional credits via a promo credit template (randomized cash payout)
     * @param {string|number} brandUserId - The brand user ID
     * @param {number} templateId - The promo credit template ID
     * @param {string} reason - Reason for granting credits (required)
     * @param {string} fundType - Where funds land: "converted" or "unconverted" (default: unconverted)
     * @returns {Promise<Object>} Response with promo_request_id, new_promo_balance, etc.
     */
    async grantRMGCreditsFromTemplate(brandUserId, templateId, reason, fundType = "unconverted") {
        try {
            const payload = {
                template_id: templateId,
                fund_type: fundType,
                reason: reason
            };

            const response = await axios.post(`${this.baseurl}/admin/users/${brandUserId}/rmg-credit`, payload);
            return response.data;
        } catch (error) {
            console.error("Failed to grant RMG credits from template:", error);
            throw new Error(error?.response?.data?.message || "Failed to grant RMG credits from template");
        }
    }

    /**
     * Retract RMG promotional credits from a user via Amwest API
     * @param {string|number} brandUserId - The brand user ID
     * @param {string} credits - Number of credits to retract (minimum: 1)
     * @param {string} cashAmount - Optional cash amount to retract (defaults to "0")
     * @param {string} reason - Reason for retracting credits
     * @param {string} description - Optional description
     * @param {string} referenceId - Optional idempotency key
     * @returns {Promise<Object>} - Response with promo_request_id, amwest_transaction_id, new_promo_balance
     */
    async retractRMGCredits(brandUserId, credits, cashAmount = "0", reason = "", description = "", referenceId = "") {
        try {
            const payload = {
                credits: credits,
                cash_amount: cashAmount,
                reason: reason
            };

            if (description) {
                payload.description = description;
            }

            if (referenceId) {
                payload.reference_id = referenceId;
            }

            const response = await axios.delete(`${this.baseurl}/admin/users/${brandUserId}/rmg-credit`, {
                data: payload
            });
            return response.data;
        } catch (error) {
            console.error("Failed to retract RMG credits:", error);
            throw new Error(error?.response?.data?.message || "Failed to retract RMG credits");
        }
    }

    // ===== LOYALTY HISTORY =====

    async getUserLoyaltyHistory(brandUserId, limit = 50, page = 1, filters = {}) {
        try {
            const params = {
                limit: limit,
                page: page,
                ...filters
            };
            const response = await axios.get(`${this.baseurl}/admin/users/${brandUserId}/loyalty-history`, {
                params: params
            });
            // Return the full response with results and total for lazy pagination
            return response.data;
        } catch (error) {
            console.error("Failed to get loyalty history:", error);
            throw new Error(error?.response?.data?.message || "Failed to get loyalty history");
        }
    }

    // ===== CASINO ROUND HISTORY =====

    async getUserCasinoRoundHistory(brandUserId, limit = 50, page = 1, filters = {}) {
        try {
            const params = {
                limit: limit,
                page: page,
                ...filters
            };
            const response = await axios.get(`${this.baseurl}/admin/users/${brandUserId}/casino-rounds`, {
                params: params
            });
            // Return the results array directly for backward compatibility
            return response.data.results || response.data;
        } catch (error) {
            console.error("Failed to get casino round history:", error);
            throw new Error(error?.response?.data?.message || "Failed to get casino round history");
        }
    }

    /**
     * Get game transaction history for a user (works for all providers including AmWest)
     * This queries ledger.game_transaction directly instead of game rounds
     */
    async getUserGameTransactionHistory(brandUserId, limit = 50, page = 1, filters = {}) {
        try {
            const params = {
                limit: limit,
                page: page,
                ...filters
            };
            const response = await axios.get(`${this.baseurl}/admin/users/${brandUserId}/game-transactions`, {
                params: params
            });
            return response.data;
        } catch (error) {
            console.error("Failed to get game transaction history:", error);
            throw new Error(error?.response?.data?.message || "Failed to get game transaction history");
        }
    }

    async exportGameTransactionHistoryCSV(brandUserId, filters = {}) {
        try {
            const response = await axios.get(`${this.baseurl}/admin/users/${brandUserId}/game-transactions/export`, {
                params: filters,
                responseType: "blob"
            });
            return response.data;
        } catch (error) {
            console.error("Failed to export game transaction history CSV:", error);
            let message = "Failed to export game transaction history CSV";
            const data = error?.response?.data;
            if (data instanceof Blob) {
                try {
                    const parsed = JSON.parse(await data.text());
                    if (parsed?.message) message = parsed.message;
                } catch { /* ignore parse error */ }
            }
            throw new Error(message);
        }
    }

    // ===== TRANSACTION HISTORY =====

    async getUserTransactionHistory(brandUserId, limit = 50, page = 1, filters = {}) {
        try {
            const params = {
                limit: limit,
                page: page,
                ...filters
            };
            const response = await axios.get(`${this.baseurl}/admin/users/${brandUserId}/transactions`, {
                params: params
            });
            // Return the full response with results, total, and meta
            return response.data;
        } catch (error) {
            console.error("Failed to get transaction history:", error);
            throw new Error(error?.response?.data?.message || "Failed to get transaction history");
        }
    }

    async exportTransactionHistoryCSV(brandUserId, filters = {}) {
        try {
            const response = await axios.get(`${this.baseurl}/admin/users/${brandUserId}/transactions/export`, {
                params: filters,
                responseType: "blob"
            });
            return response.data;
        } catch (error) {
            console.error("Failed to export transaction history CSV:", error);
            let message = "Failed to export transaction history CSV";
            const data = error?.response?.data;
            if (data instanceof Blob) {
                try {
                    const parsed = JSON.parse(await data.text());
                    if (parsed?.message) message = parsed.message;
                } catch { /* ignore parse error */ }
            }
            throw new Error(message);
        }
    }

    async getAmwestTransactionHistory(brandUserId, dateFrom, dateTo, page = 1, pageSize = 50, filters = {}) {
        try {
            const params = {
                date_from: dateFrom,
                date_to: dateTo,
                page: page,
                page_size: pageSize,
                ...filters
            };
            const response = await axios.get(`${this.baseurl}/admin/user/${brandUserId}/amwest-transactions`, {
                params: params
            });
            return response.data;
        } catch (error) {
            console.error("Failed to fetch Amwest transaction history:", error);
            throw error;
        }
    }

    async getUserPlayedGames(brandUserId) {
        try {
            const response = await axios.get(`${this.baseurl}/admin/user/${brandUserId}/played-games`);
            return response.data;
        } catch (error) {
            console.error("Failed to fetch played games:", error);
            throw error;
        }
    }

    async getRemotePlayHistory(brandUserId, gameVersionId, dateFrom, dateTo, page = 1, pageSize = 50) {
        try {
            const params = {
                game_version_id: gameVersionId,
                date_from: dateFrom,
                date_to: dateTo,
                page: page,
                page_size: pageSize
            };
            const response = await axios.get(`${this.baseurl}/admin/user/${brandUserId}/remote-play-history`, {
                params: params
            });
            return response.data;
        } catch (error) {
            console.error("Failed to fetch remote play history:", error);
            throw error;
        }
    }

    async getAmwestWithdrawalHistory(brandUserId, dateFrom, dateTo, page = 1, pageSize = 50, status = null) {
        try {
            const params = {
                date_from: dateFrom,
                date_to: dateTo,
                page: page,
                page_size: pageSize
            };
            if (status !== null && status !== undefined) {
                params.status = status;
            }
            const response = await axios.get(`${this.baseurl}/admin/user/${brandUserId}/amwest-withdrawal-history`, {
                params: params
            });
            return response.data;
        } catch (error) {
            console.error("Failed to fetch Amwest withdrawal history:", error);
            throw error;
        }
    }

    async fetchRawTransactionDetails(brandUserId, khkTransactionId) {
        try {
            const response = await axios.get(`${this.baseurl}/admin/users/${brandUserId}/transactions/${encodeURIComponent(khkTransactionId)}/details`);
            return response.data;
        } catch (error) {
            console.error("Failed to get raw transaction details:", error);
            throw new Error(error?.response?.data?.message || "Failed to get raw transaction details");
        }
    }

    // ===== USER ACCESS HISTORY (Sessions) =====

    /**
     * Get user access history (session/login history)
     * @param {number} brandUserId - The brand user ID
     * @param {number} limit - Maximum number of records to return (default: 100)
     * @returns {Promise<Object>} Access history with device info
     */
    async getUserAccessHistory(brandUserId, { limit = 20, page = 1, device_type, country } = {}) {
        try {
            const params = { limit, page };
            if (device_type) params.device_type = device_type;
            if (country) params.country = country;
            const response = await axios.get(`${this.baseurl}/admin/users/${brandUserId}/access-history`, {
                params
            });
            return response.data;
        } catch (error) {
            console.error("Failed to get user access history:", error);
            throw new Error(error?.response?.data?.message || "Failed to get user access history");
        }
    }

    /**
     * Get user devices
     * @param {number} brandUserId - The brand user ID
     * @returns {Promise<Object>} User devices with details
     */
    async getUserDevices(brandUserId, { limit = 20, page = 1, device_type } = {}) {
        try {
            const params = { limit, page };
            if (device_type) params.device_type = device_type;
            const response = await axios.get(`${this.baseurl}/admin/users/${brandUserId}/devices`, {
                params
            });
            return response.data;
        } catch (error) {
            console.error("Failed to get user devices:", error);
            throw new Error(error?.response?.data?.message || "Failed to get user devices");
        }
    }

    async getUserVendorAccounts(brandUserId) {
        try {
            const response = await axios.get(`${this.baseurl}/admin/users/${brandUserId}/vendor-accounts`);
            return response.data;
        } catch (error) {
            console.error("Failed to get vendor accounts:", error);
            throw new Error(error?.response?.data?.message || "Failed to get vendor accounts");
        }
    }

    /**
     * Get user risk events (fraud alerts)
     * @param {number} brandUserId - The brand user ID
     * @returns {Promise<Object>} Risk events for the user
     */
    async getUserRiskEvents(brandUserId, { limit = 20, page = 1, event_type, severity } = {}) {
        try {
            const params = { limit, page };
            if (event_type) params.event_type = event_type;
            if (severity) params.severity = severity;
            const response = await axios.get(`${this.baseurl}/admin/users/${brandUserId}/risk-events`, {
                params
            });
            return response.data;
        } catch (error) {
            console.error("Failed to get user risk events:", error);
            throw new Error(error?.response?.data?.message || "Failed to get user risk events");
        }
    }

    // ===== RISK PROFILES =====

    /**
     * Fetch all risk profiles
     * @returns {Promise<Array>} List of risk profiles
     */
    async fetchRiskProfiles() {
        try {
            const response = await axios.get(`${this.baseurl}/admin/risk/profiles`);
            return response.data?.data || response.data || [];
        } catch (error) {
            console.error("Failed to fetch risk profiles:", error);
            throw new Error(error?.response?.data?.message || "Failed to fetch risk profiles");
        }
    }

    /**
     * Fetch limits for a specific risk profile
     * @param {number} profileId - The risk profile ID
     * @returns {Promise<Array>} List of profile limits
     */
    async fetchRiskProfileLimits(profileId) {
        try {
            const response = await axios.get(`${this.baseurl}/admin/risk/profiles/${profileId}/limits`);
            return response.data || [];
        } catch (error) {
            console.error("Failed to fetch risk profile limits:", error);
            throw new Error(error?.response?.data?.message || "Failed to fetch risk profile limits");
        }
    }

    /**
     * Add a limit to a risk profile
     * @param {number} profileId - The risk profile ID
     * @param {Object} limitData - The limit data { limit_type_id, limit_amount, currency_mode_id }
     * @returns {Promise<Object>} Created limit
     */
    async addRiskProfileLimit(profileId, limitData) {
        try {
            const response = await axios.post(`${this.baseurl}/admin/risk/profiles/${profileId}/limits`, limitData);
            return response.data;
        } catch (error) {
            console.error("Failed to add risk profile limit:", error);
            throw new Error(error?.response?.data?.message || "Failed to add risk profile limit");
        }
    }

    /**
     * Update a risk profile limit
     * @param {number} profileId - The risk profile ID
     * @param {number} limitId - The limit ID
     * @param {Object} limitData - The limit data { limit_amount }
     * @returns {Promise<Object>} Updated limit
     */
    async updateRiskProfileLimit(profileId, limitId, limitData) {
        try {
            const response = await axios.put(
                `${this.baseurl}/admin/risk/profiles/${profileId}/limits/${limitId}`,
                limitData
            );
            return response.data;
        } catch (error) {
            console.error("Failed to update risk profile limit:", error);
            throw new Error(error?.response?.data?.message || "Failed to update risk profile limit");
        }
    }

    // ===== USER LOCKS (BLOCKING) =====

    /**
     * Block a user permanently (admin ban)
     * @param {number} brandUserId - The brand user ID
     * @param {string} reason - Reason for blocking
     * @returns {Promise<Object>} Created lock details
     */
    async blockUser(brandUserId, reason = "Admin blocked user") {
        try {
            const response = await axios.post(`${this.baseurl}/admin/risk/user-locks`, {
                brand_user_id: brandUserId,
                lock_type: "admin_ban",
                reason: reason
            });
            return response.data;
        } catch (error) {
            console.error("Failed to block user:", error);
            throw new Error(error?.response?.data?.message || "Failed to block user");
        }
    }

    /**
     * Unblock a user by removing their admin ban lock
     * @param {number} userLockId - The lock ID to remove
     * @param {number} version - The lock version (for optimistic locking)
     * @param {string} reason - Reason for unblocking
     * @returns {Promise<Object>} Removal confirmation
     */
    async unblockUser(userLockId, version, reason = "Admin unblocked user") {
        try {
            // Use query params because AWS API Gateway strips body from DELETE requests
            const params = new URLSearchParams({
                version: version,
                removal_reason: reason
            });
            const response = await axios.delete(
                `${this.baseurl}/admin/risk/user-locks/${userLockId}?${params.toString()}`
            );
            return response.data;
        } catch (error) {
            console.error("Failed to unblock user:", error);
            throw new Error(error?.response?.data?.message || "Failed to unblock user");
        }
    }

    /**
     * Trigger a password reset email for a user
     * @param {number} brandUserId - The brand user ID
     * @returns {Promise<Object>} Success confirmation
     */
    async triggerPasswordReset(brandUserId) {
        try {
            const response = await axios.post(`${this.baseurl}/admin/users/${brandUserId}/password-reset`);
            return response.data;
        } catch (error) {
            console.error("Failed to trigger password reset:", error);
            throw new Error(error?.response?.data?.message || "Failed to trigger password reset");
        }
    }

    /**
     * Kick user (terminate all active sessions)
     * @param {number} brandUserId - The brand user ID
     * @returns {Promise<Object>} Kick result with sessions terminated count
     */
    async kickUser(brandUserId) {
        try {
            const response = await axios.post(`${this.baseurl}/admin/users/${brandUserId}/kick`);
            return response.data;
        } catch (error) {
            console.error("Failed to kick user:", error);
            throw new Error(error?.response?.data?.message || "Failed to kick user");
        }
    }

    /**
     * Get active locks for a user
     * @param {number} brandUserId - The brand user ID
     * @returns {Promise<Object>} User's active locks
     */
    async getUserLocks(brandUserId) {
        try {
            const response = await axios.get(`${this.baseurl}/admin/risk/user-locks`, {
                params: { brand_user_id: brandUserId, is_active: true }
            });
            return response.data;
        } catch (error) {
            console.error("Failed to get user locks:", error);
            throw new Error(error?.response?.data?.message || "Failed to get user locks");
        }
    }

    // ===== RESPONSIBLE GAMING =====

    /**
     * Get comprehensive responsible gaming overview for a user
     * Returns 3-tier limits (user/admin/system), breaks, reality check settings
     * @param {number} brandUserId - The brand user ID
     * @returns {Promise<Object>} Responsible gaming overview with limits and settings
     */
    async getUserResponsibleGamingOverview(brandUserId) {
        try {
            const response = await axios.get(`${this.baseurl}/admin/responsible-gaming/users/${brandUserId}`);
            return response.data;
        } catch (error) {
            console.error("Failed to get user responsible gaming overview:", error);
            throw new Error(error?.response?.data?.message || "Failed to get user responsible gaming overview");
        }
    }

    /**
     * Get user's active limits
     * @param {number} brandUserId - The brand user ID
     * @returns {Promise<Object>} User limits
     */
    async getUserLimits(brandUserId) {
        try {
            const response = await axios.get(`${this.baseurl}/admin/responsible-gaming/users/${brandUserId}/limits`);
            return response.data;
        } catch (error) {
            console.error("Failed to get user limits:", error);
            throw new Error(error?.response?.data?.message || "Failed to get user limits");
        }
    }

    /**
     * Set or update a user limit (admin override)
     * @param {number} brandUserId - The brand user ID
     * @param {Object} limitData - Limit configuration
     * @param {string} limitData.limit_type_name - e.g. "daily_purchase_limit"
     * @param {number} limitData.limit_amount - The limit amount
     * @param {number} limitData.currency_mode_id - Currency mode (optional)
     * @param {boolean} limitData.can_user_increase - Whether user can increase this limit
     * @param {string} limitData.admin_reason - Reason for the change
     * @returns {Promise<Object>} Created/updated limit
     */
    async setUserLimit(brandUserId, limitData) {
        try {
            const response = await axios.post(
                `${this.baseurl}/admin/responsible-gaming/users/${brandUserId}/limits`,
                limitData
            );
            return response.data;
        } catch (error) {
            console.error("Failed to set user limit:", error);
            throw new Error(error?.response?.data?.message || "Failed to set user limit");
        }
    }

    /**
     * Update user responsible gaming settings (reality check, session limits)
     * @param {number} brandUserId - The brand user ID
     * @param {Object} settings - Settings to update
     * @param {boolean} settings.reality_check_enabled - Enable/disable reality checks
     * @param {number} settings.reality_check_interval_minutes - Interval between checks
     * @param {number} settings.daily_session_limit_minutes - Daily session limit
     * @param {string} settings.admin_reason - Reason for the change
     * @returns {Promise<Object>} Updated settings
     */
    async updateUserResponsibleGamingSettings(brandUserId, settings) {
        try {
            const response = await axios.put(
                `${this.baseurl}/admin/responsible-gaming/users/${brandUserId}/settings`,
                settings
            );
            return response.data;
        } catch (error) {
            console.error("Failed to update user responsible gaming settings:", error);
            throw new Error(error?.response?.data?.message || "Failed to update user responsible gaming settings");
        }
    }

    /**
     * Cancel a user's break period (admin override)
     * @param {number} brandUserId - The brand user ID
     * @param {number} breakId - The break ID to cancel
     * @param {string} cancellationReason - Reason for cancellation
     * @returns {Promise<Object>} Cancelled break details
     */
    async cancelUserBreak(brandUserId, breakId, cancellationReason) {
        try {
            const response = await axios.delete(
                `${this.baseurl}/admin/responsible-gaming/users/${brandUserId}/breaks/${breakId}`,
                { data: { cancellation_reason: cancellationReason } }
            );
            return response.data;
        } catch (error) {
            console.error("Failed to cancel user break:", error);
            throw new Error(error?.response?.data?.message || "Failed to cancel user break");
        }
    }

    // ===== AUDIT LOG =====

    async getUserAuditLog(brandUserId, options = {}) {
        try {
            const params = {
                brand_user_id: brandUserId,
                limit: options.limit || 100,
                offset: options.offset || 0
            };
            if (options.dateFrom) params.date_from = options.dateFrom;
            if (options.dateTo) params.date_to = options.dateTo;
            if (options.actionType) params.action_type = options.actionType;
            if (options.entityType) params.entity_type = options.entityType;

            const response = await axios.get(`${this.baseurl}/admin/risk/audit`, { params });
            return response.data;
        } catch (error) {
            console.error("Failed to get user audit log:", error);
            throw new Error(error?.response?.data?.message || "Failed to get user audit log");
        }
    }

    // ===== WITHDRAWALS =====

    /**
     * Search withdrawal requests with filters, sorting, and pagination
     * @param {Object} params - Search parameters
     * @param {number} params.page - Page number (1-based)
     * @param {number} params.limit - Number of records per page
     * @param {string} params.sort_field - Field to sort by
     * @param {string} params.sort_order - Sort order (asc/desc)
     * @param {string} params.status - Filter by status
     * @param {string} params.provider_code - Filter by provider code
     * @param {number} params.brand_user_id - Filter by user ID
     * @param {string} params.user_display_name - Filter by display name
     * @param {string} params.user_email - Filter by user email
     * @param {string} params.assigned_to - Filter by assigned admin
     * @param {boolean} params.is_vip - Filter by VIP status
     * @param {string} params.start_date - Filter by start date (YYYY-MM-DD)
     * @param {string} params.end_date - Filter by end date (YYYY-MM-DD)
     * @param {number} params.min_amount - Filter by minimum amount
     * @param {number} params.max_amount - Filter by maximum amount
     * @param {string} params.khk_transaction_id - Filter by KHK transaction ID
     * @param {number} params.pii_search_limit - Limit for PII search results
     * @returns {Promise<Object>} Withdrawal requests with total count
     */
    async searchWithdrawals(params = {}) {
        try {
            const response = await axios.get(`${this.baseurl}/admin/withdrawals/search`, {
                params: {
                    page: params.page || 1,
                    limit: params.limit || 20,
                    sort_field: params.sort_field,
                    sort_order: params.sort_order,
                    status: params.status,
                    provider_code: params.provider_code,
                    brand_user_id: params.brand_user_id,
                    user_display_name: params.user_display_name,
                    user_email: params.user_email,
                    assigned_to: params.assigned_to,
                    is_vip: params.is_vip,
                    on_hold: params.on_hold,
                    start_date: params.start_date,
                    end_date: params.end_date,
                    min_amount: params.min_amount,
                    max_amount: params.max_amount,
                    khk_transaction_id: params.khk_transaction_id,
                    pii_search_limit: params.pii_search_limit,
                    view: params.view
                }
            });
            return response.data;
        } catch (error) {
            console.error("Failed to search withdrawals:", error);
            throw new Error(error?.response?.data?.message || "Failed to search withdrawals");
        }
    }

    /**
     * Get withdrawal detail including payment verification data
     * @param {number} withdrawalId - The withdrawal request ID
     * @returns {Promise<Object>} Withdrawal detail with payment_verification
     */
    async getWithdrawalDetail(withdrawalId) {
        try {
            const response = await axios.get(`${this.baseurl}/admin/withdrawals/${withdrawalId}`);
            return response.data;
        } catch (error) {
            console.error("Failed to fetch withdrawal detail:", error);
            throw new Error(error?.response?.data?.message || "Failed to fetch withdrawal detail");
        }
    }

    /**
     * Process a withdrawal action (approve or reject)
     * @param {number} withdrawalId - The withdrawal request ID
     * @param {string} action - "approve" or "reject"
     * @param {string} reason - Required for rejection, optional for approval
     * @returns {Promise<Object>} Action result
     */
    async processWithdrawalAction(withdrawalId, action, reason = "") {
        try {
            const response = await axios.post(`${this.baseurl}/admin/withdrawals/${withdrawalId}/action`, {
                action,
                reason
            });
            return response.data;
        } catch (error) {
            console.error("Failed to process withdrawal action:", error);
            throw new Error(error?.response?.data?.message || "Failed to process withdrawal action");
        }
    }

    /**
     * Assign a withdrawal to an admin user
     * @param {number} withdrawalId - The withdrawal request ID
     * @param {string} notes - Optional assignment notes
     * @param {number|null} assignToUserId - Optional admin user ID to assign to (defaults to self)
     * @returns {Promise<Object>} Assignment result
     */
    async assignWithdrawal(withdrawalId, notes = "", assignToUserId = null) {
        try {
            const body = { notes };
            if (assignToUserId) body.assign_to_user_id = assignToUserId;
            const response = await axios.post(`${this.baseurl}/admin/withdrawals/${withdrawalId}/assign`, body);
            return response.data;
        } catch (error) {
            console.error("Failed to assign withdrawal:", error);
            throw new Error(error?.response?.data?.message || "Failed to assign withdrawal");
        }
    }

    /**
     * Unassign a withdrawal
     * @param {number} withdrawalId - The withdrawal request ID
     * @param {string} reason - Optional reason for unassignment
     * @returns {Promise<Object>} Unassignment result
     */
    async unassignWithdrawal(withdrawalId, reason = "") {
        try {
            const response = await axios.post(`${this.baseurl}/admin/withdrawals/${withdrawalId}/unassign`, {
                reason
            });
            return response.data;
        } catch (error) {
            console.error("Failed to unassign withdrawal:", error);
            throw new Error(error?.response?.data?.message || "Failed to unassign withdrawal");
        }
    }

    async setWithdrawalOnHold(withdrawalId, onHold, reason = "") {
        try {
            const response = await axios.post(`${this.baseurl}/admin/withdrawals/${withdrawalId}/hold`, {
                on_hold: onHold,
                reason
            });
            return response.data;
        } catch (error) {
            console.error("Failed to update withdrawal hold status:", error);
            throw new Error(error?.response?.data?.message || "Failed to update hold status");
        }
    }

    async getUserRiskSummary(brandUserId) {
        try {
            const response = await axios.get(`${this.baseurl}/admin/users/${brandUserId}/risk-summary`);
            return response.data;
        } catch (error) {
            console.error("Failed to load risk summary:", error);
            throw new Error(error?.response?.data?.message || "Failed to load risk summary");
        }
    }

    async upsertReceivedDocument(brandUserId, paymentHandleId, documentType, { reference = "", notes = "" } = {}) {
        try {
            const response = await axios.post(
                `${this.baseurl}/admin/users/${brandUserId}/cards/${paymentHandleId}/received-documents/${documentType}`,
                { reference, notes }
            );
            return response.data;
        } catch (error) {
            console.error("Failed to record document receipt:", error);
            throw new Error(error?.response?.data?.message || "Failed to record document receipt");
        }
    }

    async deleteReceivedDocument(brandUserId, paymentHandleId, documentType) {
        try {
            await axios.delete(
                `${this.baseurl}/admin/users/${brandUserId}/cards/${paymentHandleId}/received-documents/${documentType}`
            );
        } catch (error) {
            console.error("Failed to clear document receipt:", error);
            throw new Error(error?.response?.data?.message || "Failed to clear document receipt");
        }
    }

    // Clears the auto-suspend state on a card (#1877) so the user can deposit with
    // it again. Brand is resolved from the admin session on the backend.
    async unlockPaymentHandle(paymentHandleId) {
        try {
            const response = await axios.post(
                `${this.baseurl}/admin/payment-handles/${paymentHandleId}/unlock`
            );
            return response.data;
        } catch (error) {
            console.error("Failed to unlock card:", error);
            throw new Error(error?.response?.data?.message || "Failed to unlock card");
        }
    }

    async upsertUserReceivedDocument(brandUserId, documentType, { reference = "", notes = "" } = {}) {
        try {
            const response = await axios.post(
                `${this.baseurl}/admin/users/${brandUserId}/received-documents/${documentType}`,
                { reference, notes }
            );
            return response.data;
        } catch (error) {
            console.error("Failed to record document receipt:", error);
            throw new Error(error?.response?.data?.message || "Failed to record document receipt");
        }
    }

    async deleteUserReceivedDocument(brandUserId, documentType) {
        try {
            await axios.delete(
                `${this.baseurl}/admin/users/${brandUserId}/received-documents/${documentType}`
            );
        } catch (error) {
            console.error("Failed to clear document receipt:", error);
            throw new Error(error?.response?.data?.message || "Failed to clear document receipt");
        }
    }

    /**
     * Get audit log entries for a withdrawal
     * @param {number} withdrawalId - The withdrawal request ID
     * @returns {Promise<Object>} Audit log entries
     */
    async getWithdrawalAuditLog(withdrawalId) {
        try {
            const response = await axios.get(`${this.baseurl}/admin/risk/audit`, {
                params: {
                    entity_type: "withdrawal",
                    entity_id: withdrawalId,
                    limit: 100,
                    offset: 0
                }
            });
            return response.data;
        } catch (error) {
            console.error("Failed to get withdrawal audit log:", error);
            throw new Error(error?.response?.data?.message || "Failed to get withdrawal audit log");
        }
    }

    /**
     * Get the withdrawal-automation engine's decision history for a withdrawal.
     * Returns rows from wallets.withdrawal_automation_decision (decision + last_mile_check
     * phases) chronologically, with failing rules and JSONB evaluation context. Drives
     * the "Automation Decisions" timeline tab on the admin detail dialog (#1901).
     * @param {number} withdrawalId
     * @returns {Promise<{decisions: Array}>}
     */
    async getWithdrawalAutomationDecisions(withdrawalId) {
        try {
            const response = await axios.get(`${this.baseurl}/admin/withdrawals/${withdrawalId}/automation-decisions`);
            return response.data;
        } catch (error) {
            console.error("Failed to get withdrawal automation decisions:", error);
            throw new Error(error?.response?.data?.message || "Failed to get withdrawal automation decisions");
        }
    }

    /**
     * Returns per-tab totals for the withdrawals queue automation tabs so the
     * inactive tabs can also show their depth (#1901).
     * @returns {Promise<{manual_review: number, auto_pending: number, auto_approved_today: number}>}
     */
    async getWithdrawalAutomationCounts() {
        try {
            const response = await axios.get(`${this.baseurl}/admin/withdrawals/automation-counts`);
            return response.data;
        } catch (error) {
            console.error("Failed to get withdrawal automation counts:", error);
            throw new Error(error?.response?.data?.message || "Failed to get withdrawal automation counts");
        }
    }

    /**
     * Get audit log entries for a KYC verification
     * @param {number} verificationId - The KYC verification ID
     * @returns {Promise<Object>} Audit log entries
     */
    async getVerificationAuditLog(verificationId) {
        try {
            const response = await axios.get(`${this.baseurl}/admin/risk/audit`, {
                params: {
                    entity_type: "kyc_verification",
                    entity_id: verificationId,
                    limit: 100,
                    offset: 0
                }
            });
            return response.data;
        } catch (error) {
            console.error("Failed to get verification audit log:", error);
            throw new Error(error?.response?.data?.message || "Failed to get verification audit log");
        }
    }

    /**
     * Get audit log entries for a duplicate account match
     * @param {number} matchId - The duplicate account match ID
     * @returns {Promise<Object>} Audit log entries
     */
    async getDuplicateMatchAuditLog(matchId, { limit = 100, offset = 0 } = {}) {
        try {
            const response = await axios.get(`${this.baseurl}/admin/risk/audit`, {
                params: {
                    entity_type: "duplicate_account_match",
                    entity_id: matchId,
                    limit,
                    offset
                }
            });
            return response.data;
        } catch (error) {
            console.error("Failed to get duplicate match audit log:", error);
            throw new Error(error?.response?.data?.message || "Failed to get duplicate match audit log");
        }
    }

    // ===== PAYOUT REVIEW =====

    /**
     * Search payout review items with filters and pagination
     * @param {Object} params - Search parameters
     * @returns {Promise<Object>} Review items with total count
     */
    async getPayoutReviewItems(params = {}) {
        try {
            const response = await axios.get(`${this.baseurl}/admin/payout-review`, {
                params: {
                    page: params.page || 1,
                    limit: params.limit || 20,
                    sort_field: params.sort_field,
                    sort_order: params.sort_order,
                    status: params.status,
                    reason: params.reason,
                    provider_code: params.provider_code
                }
            });
            return response.data;
        } catch (error) {
            console.error("Failed to get payout review items:", error);
            throw new Error(error?.response?.data?.message || "Failed to get payout review items");
        }
    }

    /**
     * Resolve a payout review item
     * @param {number} id - The review item ID
     * @param {string} action - Resolution action (no_action_needed, cancel_and_refund, mark_for_retry)
     * @param {string} notes - Resolution notes
     * @returns {Promise<Object>} Resolution result
     */
    async resolvePayoutReviewItem(id, action, notes = "") {
        try {
            const response = await axios.post(`${this.baseurl}/admin/payout-review/${id}/resolve`, {
                action,
                notes
            });
            return response.data;
        } catch (error) {
            console.error("Failed to resolve payout review item:", error);
            throw new Error(error?.response?.data?.message || "Failed to resolve payout review item");
        }
    }

    // ===== USER NOTES =====

    /**
     * Get all notes for a user
     * @param {number} brandUserId - The user's brand_user_id
     * @param {Object} [options] - Optional filters
     * @param {number} [options.withdrawalRequestId] - Filter by withdrawal request ID
     * @param {number} [options.kycVerificationId] - Filter by KYC verification ID
     * @returns {Promise<Object>} Notes list with total count
     */
    async getUserNotes(brandUserId, { withdrawalRequestId, kycVerificationId, duplicateMatchId, limit = 20, page = 1 } = {}) {
        try {
            const params = { limit, page };
            if (withdrawalRequestId) params.withdrawal_request_id = withdrawalRequestId;
            else if (kycVerificationId) params.kyc_verification_id = kycVerificationId;
            else if (duplicateMatchId) params.duplicate_match_id = duplicateMatchId;
            const response = await axios.get(`${this.baseurl}/admin/users/${brandUserId}/notes`, { params });
            return response.data;
        } catch (error) {
            console.error("Failed to get user notes:", error);
            throw new Error(error?.response?.data?.message || "Failed to get user notes");
        }
    }

    /**
     * Create a new note for a user
     * @param {number} brandUserId - The user's brand_user_id
     * @param {string} content - The note content
     * @param {Object} [options] - Optional fields
     * @param {number} [options.withdrawalRequestId] - Link note to a withdrawal request
     * @param {number} [options.kycVerificationId] - Link note to a KYC verification
     * @returns {Promise<Object>} Created note
     */
    async createUserNote(brandUserId, content, { withdrawalRequestId, kycVerificationId, duplicateMatchId } = {}) {
        try {
            const body = { content };
            if (withdrawalRequestId) {
                body.withdrawal_request_id = withdrawalRequestId;
            }
            if (kycVerificationId) {
                body.kyc_verification_id = kycVerificationId;
            }
            if (duplicateMatchId) {
                body.duplicate_match_id = duplicateMatchId;
            }
            const response = await axios.post(`${this.baseurl}/admin/users/${brandUserId}/notes`, body);
            return response.data;
        } catch (error) {
            console.error("Failed to create user note:", error);
            throw new Error(error?.response?.data?.message || "Failed to create user note");
        }
    }

    /**
     * Delete a user note (soft delete)
     * @param {number} brandUserId - The user's brand_user_id
     * @param {number} noteId - The note ID to delete
     * @returns {Promise<Object>} Deletion result
     */
    async deleteUserNote(brandUserId, noteId) {
        try {
            const response = await axios.delete(`${this.baseurl}/admin/users/${brandUserId}/notes/${noteId}`);
            return response.data;
        } catch (error) {
            console.error("Failed to delete user note:", error);
            throw new Error(error?.response?.data?.message || "Failed to delete user note");
        }
    }

    // ==================== Promo Credit Templates ====================

    /**
     * Fetch all promo credit templates
     * @returns {Promise<Object>} List of templates with count
     */
    async fetchPromoCreditTemplates() {
        try {
            const response = await axios.get(`${this.baseurl}/admin/promo-credit-templates`);
            return response.data;
        } catch (error) {
            console.error("Failed to fetch promo credit templates:", error);
            throw new Error(error?.response?.data?.message || "Failed to fetch promo credit templates");
        }
    }

    /**
     * Fetch a promo credit template by ID
     * @param {number} templateId - The template ID
     * @returns {Promise<Object>} Template details with outcomes
     */
    async fetchPromoCreditTemplateById(templateId) {
        try {
            const response = await axios.get(`${this.baseurl}/admin/promo-credit-templates/${templateId}`);
            return response.data;
        } catch (error) {
            console.error("Failed to fetch promo credit template:", error);
            throw new Error(error?.response?.data?.message || "Failed to fetch promo credit template");
        }
    }

    /**
     * Create a new promo credit template
     * @param {Object} templateData - Template data with name, description, credits, and outcomes
     * @returns {Promise<Object>} Created template
     */
    async createPromoCreditTemplate(templateData) {
        try {
            const response = await axios.post(`${this.baseurl}/admin/promo-credit-templates`, templateData);
            return response.data;
        } catch (error) {
            console.error("Failed to create promo credit template:", error);
            throw new Error(error?.response?.data?.message || "Failed to create promo credit template");
        }
    }

    /**
     * Update a promo credit template
     * @param {number} templateId - The template ID
     * @param {Object} templateData - Updated template data
     * @returns {Promise<Object>} Updated template
     */
    async updatePromoCreditTemplate(templateId, templateData) {
        try {
            const response = await axios.put(
                `${this.baseurl}/admin/promo-credit-templates/${templateId}`,
                templateData
            );
            return response.data;
        } catch (error) {
            console.error("Failed to update promo credit template:", error);
            throw new Error(error?.response?.data?.message || "Failed to update promo credit template");
        }
    }

    /**
     * Delete a promo credit template
     * @param {number} templateId - The template ID
     * @returns {Promise<Object>} Deletion result
     */
    async deletePromoCreditTemplate(templateId) {
        try {
            const response = await axios.delete(`${this.baseurl}/admin/promo-credit-templates/${templateId}`);
            return response.data;
        } catch (error) {
            console.error("Failed to delete promo credit template:", error);
            throw new Error(error?.response?.data?.message || "Failed to delete promo credit template");
        }
    }

    // ==================== Anti-Fraud / Duplicate Detection ====================

    /**
     * Assign a duplicate match for review
     * @param {number} matchId - The match to assign
     * @param {string} [notes=""] - Optional assignment notes
     * @param {number|null} [assignToUserId=null] - Admin user ID to assign to (null = self)
     * @returns {Promise<Object>} Assignment result
     */
    async assignDuplicateMatch(matchId, notes = "", assignToUserId = null) {
        try {
            const body = { notes };
            if (assignToUserId) body.assign_to_user_id = assignToUserId;
            const response = await axios.post(`${this.baseurl}/admin/risk/duplicates/${matchId}/assign`, body);
            return response.data;
        } catch (error) {
            console.error("Failed to assign duplicate match:", error);
            throw new Error(error?.response?.data?.message || "Failed to assign duplicate match");
        }
    }

    async unassignDuplicateMatch(matchId, reason = "") {
        try {
            const response = await axios.post(`${this.baseurl}/admin/risk/duplicates/${matchId}/unassign`, {
                reason
            });
            return response.data;
        } catch (error) {
            console.error("Failed to unassign duplicate match:", error);
            throw new Error(error?.response?.data?.message || "Failed to unassign duplicate match");
        }
    }

    async fetchDuplicateMatches(params = {}) {
        try {
            const queryParams = new URLSearchParams();
            if (params.brand_id) queryParams.append("brand_id", params.brand_id);
            if (params.page) queryParams.append("page", params.page);
            if (params.limit) queryParams.append("limit", params.limit);
            if (params.status) queryParams.append("status", params.status);
            if (params.match_type) queryParams.append("match_type", params.match_type);
            if (params.confidence) queryParams.append("confidence", params.confidence);
            if (params.match_id) queryParams.append("match_id", params.match_id);
            if (params.user_email) queryParams.append("user_email", params.user_email);
            if (params.sort_field) queryParams.append("sort_field", params.sort_field);
            if (params.sort_order) queryParams.append("sort_order", params.sort_order);

            const url = `${this.baseurl}/admin/risk/duplicates${queryParams.toString() ? "?" + queryParams.toString() : ""}`;
            const response = await axios.get(url);
            return response.data;
        } catch (error) {
            console.error("Failed to fetch duplicate matches:", error);
            throw new Error(error?.response?.data?.message || "Failed to fetch duplicate matches");
        }
    }

    /**
     * Get available status options for duplicate matches
     * @returns {Promise<Array>} List of status options with label and value
     */
    async getDuplicateMatchStatuses() {
        try {
            const response = await axios.get(`${this.baseurl}/admin/risk/duplicates/statuses`);
            return response.data;
        } catch (error) {
            console.error("Failed to fetch duplicate match statuses:", error);
            throw new Error(error?.response?.data?.message || "Failed to fetch duplicate match statuses");
        }
    }

    /**
     * Count pending duplicate matches
     * @param {number} [brandId] - Filter by brand ID
     * @returns {Promise<Object>} Object with count property
     */
    async countPendingDuplicates(brandId = null) {
        try {
            const url = brandId
                ? `${this.baseurl}/admin/risk/duplicates/count?brand_id=${brandId}`
                : `${this.baseurl}/admin/risk/duplicates/count`;
            const response = await axios.get(url);
            return response.data;
        } catch (error) {
            console.error("Failed to count pending duplicates:", error);
            throw new Error(error?.response?.data?.message || "Failed to count pending duplicates");
        }
    }

    /**
     * Resolve a duplicate match (confirm, dismiss, or mark as false positive)
     * @param {number} matchId - The duplicate match ID
     * @param {Object} resolution - Resolution details
     * @param {string} resolution.status - One of: "confirmed_duplicate", "false_positive", "dismissed"
     * @param {string} [resolution.notes] - Resolution notes
     * @param {boolean} [resolution.apply_lock=false] - If confirming, whether to lock the duplicate account
     * @param {number} [resolution.duplicate_brand_user_id] - Which account of the match is the
     *   duplicate being closed. Defaults server-side to the newer account. The other account is
     *   treated as the keeper and has its duplicate_account lock released.
     * @returns {Promise<Object>} Resolution result
     */
    async resolveDuplicateMatch(matchId, resolution) {
        try {
            const response = await axios.post(`${this.baseurl}/admin/risk/duplicates/${matchId}/resolve`, resolution);
            return response.data;
        } catch (error) {
            console.error("Failed to resolve duplicate match:", error);
            throw new Error(error?.response?.data?.message || "Failed to resolve duplicate match");
        }
    }

    /**
     * Get duplicate matches for a specific user
     * @param {number} brandUserId - The user's brand_user_id
     * @returns {Promise<Array>} List of duplicate matches involving this user
     */
    async fetchUserDuplicates(brandUserId) {
        try {
            const response = await axios.get(`${this.baseurl}/admin/users/${brandUserId}/duplicates`);
            return response.data;
        } catch (error) {
            console.error("Failed to fetch user duplicates:", error);
            throw new Error(error?.response?.data?.message || "Failed to fetch user duplicates");
        }
    }

    // ===== SITEWIDE NOTIFICATIONS =====

    /**
     * List all sitewide notifications for the current brand
     * @returns {Promise<Object>} Object containing notifications array
     */
    async listSitewideNotifications() {
        try {
            const response = await axios.get(`${this.baseurl}/admin/notifications`);
            return response.data;
        } catch (error) {
            console.error("Failed to fetch sitewide notifications:", error);
            throw new Error(error?.response?.data?.message || "Failed to fetch sitewide notifications");
        }
    }

    /**
     * Get a specific sitewide notification by ID
     * @param {number} id - The notification ID
     * @returns {Promise<Object>} The notification object
     */
    async getSitewideNotification(id) {
        try {
            const response = await axios.get(`${this.baseurl}/admin/notifications/${id}`);
            return response.data;
        } catch (error) {
            console.error("Failed to fetch sitewide notification:", error);
            throw new Error(error?.response?.data?.message || "Failed to fetch sitewide notification");
        }
    }

    /**
     * Create a new sitewide notification
     * @param {Object} data - Notification data
     * @param {string} data.name - Notification name/title
     * @param {string} data.message - Notification message
     * @param {string} data.log_level - Log level (error, warning, info, success)
     * @param {string} data.display_option - Display option (login, lobby, persistent)
     * @param {boolean} data.is_active - Whether the notification is active
     * @param {string} [data.start_at] - Start time (ISO string)
     * @param {string} [data.end_at] - End time (ISO string)
     * @returns {Promise<Object>} The created notification
     */
    async createSitewideNotification(data) {
        try {
            const response = await axios.post(`${this.baseurl}/admin/notifications`, data);
            return response.data;
        } catch (error) {
            console.error("Failed to create sitewide notification:", error);
            throw new Error(error?.response?.data?.message || "Failed to create sitewide notification");
        }
    }

    /**
     * Update an existing sitewide notification
     * @param {number} id - The notification ID
     * @param {Object} data - Updated notification data
     * @returns {Promise<Object>} The updated notification
     */
    async updateSitewideNotification(id, data) {
        try {
            const response = await axios.put(`${this.baseurl}/admin/notifications/${id}`, data);
            return response.data;
        } catch (error) {
            console.error("Failed to update sitewide notification:", error);
            throw new Error(error?.response?.data?.message || "Failed to update sitewide notification");
        }
    }

    /**
     * Delete a sitewide notification
     * @param {number} id - The notification ID
     * @returns {Promise<Object>} Deletion result
     */
    async deleteSitewideNotification(id) {
        try {
            const response = await axios.delete(`${this.baseurl}/admin/notifications/${id}`);
            return response.data;
        } catch (error) {
            console.error("Failed to delete sitewide notification:", error);
            throw new Error(error?.response?.data?.message || "Failed to delete sitewide notification");
        }
    }

    // ===== LEGAL DOCUMENTS =====

    async listLegalDocuments() {
        try {
            const response = await axios.get(`${this.baseurl}/admin/legal-documents`);
            return response.data;
        } catch (error) {
            console.error("Failed to fetch legal documents:", error);
            throw new Error(error?.response?.data?.message || "Failed to fetch legal documents");
        }
    }

    async getLegalDocument(id) {
        try {
            const response = await axios.get(`${this.baseurl}/admin/legal-documents/${id}`);
            return response.data;
        } catch (error) {
            console.error("Failed to fetch legal document:", error);
            throw new Error(error?.response?.data?.message || "Failed to fetch legal document");
        }
    }

    async createLegalDocument(payload) {
        try {
            const response = await axios.post(`${this.baseurl}/admin/legal-documents`, payload);
            return response.data;
        } catch (error) {
            console.error("Failed to create legal document:", error);
            if (error.response?.status === 409) {
                throw new Error(
                    error?.response?.data?.message ||
                    "Another version already uses that label."
                );
            }
            throw new Error(error?.response?.data?.message || "Failed to create legal document");
        }
    }

    async updateLegalDocument(id, data) {
        try {
            const response = await axios.put(`${this.baseurl}/admin/legal-documents/${id}`, data);
            return response.data;
        } catch (error) {
            console.error("Failed to update legal document:", error);
            // 409 means the row is not a draft and cannot be edited in place.
            if (error.response?.status === 409) {
                throw new Error(
                    error?.response?.data?.message ||
                    "This document is not a draft and can no longer be edited."
                );
            }
            throw new Error(error?.response?.data?.message || "Failed to update legal document");
        }
    }

    async publishLegalDocument(id, requireReacceptance = true) {
        try {
            const response = await axios.post(`${this.baseurl}/admin/legal-documents/${id}/publish`, {
                require_reacceptance: requireReacceptance
            });
            return response.data;
        } catch (error) {
            console.error("Failed to make legal document live:", error);
            throw new Error(error?.response?.data?.message || "Failed to update the live version", { cause: error });
        }
    }

    async updateLegalDocumentVersion(id, version) {
        try {
            const response = await axios.put(`${this.baseurl}/admin/legal-documents/${id}/version`, { version });
            return response.data;
        } catch (error) {
            console.error("Failed to update legal document version:", error);
            if (error.response?.status === 409) {
                throw new Error(
                    error?.response?.data?.message ||
                    "Another version already uses that label."
                );
            }
            throw new Error(error?.response?.data?.message || "Failed to update version number");
        }
    }

    async archiveLegalDocument(id) {
        try {
            const response = await axios.delete(`${this.baseurl}/admin/legal-documents/${id}`);
            return response.data;
        } catch (error) {
            console.error("Failed to archive legal document:", error);
            throw new Error(error?.response?.data?.message || "Failed to archive legal document");
        }
    }

    // summary=true renders through the summary pipeline (no auto heading IDs),
    // matching how the public site renders summary_content.
    async previewLegalDocumentMarkdown(content, summary = false) {
        try {
            const response = await axios.post(`${this.baseurl}/admin/legal-documents/preview`, { content, summary });
            return response.data?.html || "";
        } catch (error) {
            console.error("Failed to render legal document preview:", error);
            throw new Error(error?.response?.data?.message || "Failed to render preview");
        }
    }

    // Carousel Management Methods

    /**
     * List all carousel items for the current brand
     * @returns {Promise<Array>} List of carousel items
     */
    async listCarouselItems() {
        try {
            const response = await axios.get(`${this.baseurl}/admin/carousel-items`);
            return response.data.items || [];
        } catch (error) {
            console.error("Failed to list carousel items:", error);
            throw new Error(error?.response?.data?.message || "Failed to list carousel items");
        }
    }

    /**
     * Get a specific carousel item by ID
     * @param {number} itemId - The carousel item ID
     * @returns {Promise<Object>} The carousel item
     */
    async getCarouselItem(itemId) {
        try {
            const response = await axios.get(`${this.baseurl}/admin/carousel-items/${itemId}`);
            return response.data;
        } catch (error) {
            console.error("Failed to get carousel item:", error);
            throw new Error(error?.response?.data?.message || "Failed to get carousel item");
        }
    }

    /**
     * Create a new carousel item
     * @param {Object} itemData - The carousel item data
     * @returns {Promise<Object>} The created carousel item
     */
    async createCarouselItem(itemData) {
        try {
            const response = await axios.post(`${this.baseurl}/admin/carousel-items`, itemData);
            return response.data;
        } catch (error) {
            console.error("Failed to create carousel item:", error);
            throw new Error(error?.response?.data?.message || "Failed to create carousel item");
        }
    }

    /**
     * Update an existing carousel item
     * @param {number} itemId - The carousel item ID
     * @param {Object} itemData - The updated carousel item data
     * @returns {Promise<Object>} The updated carousel item
     */
    async updateCarouselItem(itemId, itemData) {
        try {
            const response = await axios.put(`${this.baseurl}/admin/carousel-items/${itemId}`, itemData);
            return response.data;
        } catch (error) {
            console.error("Failed to update carousel item:", error);
            throw new Error(error?.response?.data?.message || "Failed to update carousel item");
        }
    }

    /**
     * Delete a carousel item
     * @param {number} itemId - The carousel item ID
     * @returns {Promise<void>}
     */
    async deleteCarouselItem(itemId) {
        try {
            await axios.delete(`${this.baseurl}/admin/carousel-items/${itemId}`);
        } catch (error) {
            console.error("Failed to delete carousel item:", error);
            throw new Error(error?.response?.data?.message || "Failed to delete carousel item");
        }
    }

    /**
     * Reorder carousel items
     * @param {Array<{id: number, placement_priority: number}>} items - Items with their new priorities
     * @returns {Promise<void>}
     */
    async reorderCarouselItems(items) {
        try {
            await axios.put(`${this.baseurl}/admin/carousel-items/reorder`, { items });
        } catch (error) {
            console.error("Failed to reorder carousel items:", error);
            throw new Error(error?.response?.data?.message || "Failed to reorder carousel items");
        }
    }

    // ===== CAROUSEL ENTRIES =====

    async listCarouselEntries({ sourceType, sourceId } = {}) {
        try {
            const params = {};
            if (sourceType) params.source_type = sourceType;
            if (sourceId) params.source_id = sourceId;
            const response = await axios.get(`${this.baseurl}/admin/carousel-entries`, { params });
            return response.data.entries || [];
        } catch (error) {
            console.error("Failed to list carousel entries:", error);
            throw new Error(error?.response?.data?.message || "Failed to list carousel entries");
        }
    }

    async createCarouselEntry(entryData) {
        try {
            const response = await axios.post(`${this.baseurl}/admin/carousel-entries`, entryData);
            return response.data;
        } catch (error) {
            console.error("Failed to create carousel entry:", error);
            throw new Error(error?.response?.data?.message || "Failed to create carousel entry");
        }
    }

    async deleteCarouselEntry(entryId) {
        try {
            await axios.delete(`${this.baseurl}/admin/carousel-entries/${entryId}`);
        } catch (error) {
            console.error("Failed to delete carousel entry:", error);
            throw new Error(error?.response?.data?.message || "Failed to delete carousel entry");
        }
    }

    async reorderCarouselEntries(entries) {
        try {
            await axios.put(`${this.baseurl}/admin/carousel-entries/reorder`, { entries });
        } catch (error) {
            console.error("Failed to reorder carousel entries:", error);
            throw new Error(error?.response?.data?.message || "Failed to reorder carousel entries");
        }
    }

    async getAvailableCarouselSources(sourceType) {
        try {
            const response = await axios.get(`${this.baseurl}/admin/carousel-entries/available-sources`, {
                params: { source_type: sourceType }
            });
            return response.data.sources || [];
        } catch (error) {
            console.error("Failed to get available carousel sources:", error);
            throw new Error(error?.response?.data?.message || "Failed to get available carousel sources");
        }
    }

    // ===== FEATURE FLAGS =====

    async getFeatureFlags() {
        try {
            const response = await axios.get(`${this.baseurl}/admin/feature-flags`);
            return response.data;
        } catch (error) {
            console.error("Failed to fetch feature flags:", error);
            throw new Error(error?.response?.data?.message || "Failed to fetch feature flags");
        }
    }

    async getFeatureFlag(id) {
        try {
            const response = await axios.get(`${this.baseurl}/admin/feature-flags/${id}`);
            return response.data;
        } catch (error) {
            console.error("Failed to fetch feature flag:", error);
            throw new Error(error?.response?.data?.message || "Failed to fetch feature flag");
        }
    }

    async updateFeatureFlag(id, data) {
        try {
            const response = await axios.put(`${this.baseurl}/admin/feature-flags/${id}`, data);
            return response.data;
        } catch (error) {
            console.error("Failed to update feature flag:", error);
            throw error;
        }
    }

    // ===== PURCHASE SYNC =====

    async getPendingPurchases(userId) {
        try {
            const response = await axios.get(`${this.baseurl}/admin/users/${userId}/pending-purchases`);
            return response.data;
        } catch (error) {
            console.error("Failed to fetch pending purchases:", error);
            throw new Error(error?.response?.data?.message || "Failed to fetch pending purchases");
        }
    }

    async fetchAllPendingPurchases(page = 1, limit = 20) {
        try {
            const response = await axios.get(`${this.baseurl}/admin/pending-purchases`, {
                params: { page, limit }
            });
            return response.data;
        } catch (error) {
            console.error("Failed to fetch all pending purchases:", error);
            throw new Error(error?.response?.data?.message || "Failed to fetch pending purchases");
        }
    }

    async syncPurchase(purchaseId) {
        try {
            const response = await axios.post(`${this.baseurl}/admin/purchases/${purchaseId}/sync`);
            return response.data;
        } catch (error) {
            console.error("Failed to sync purchase:", error);
            throw new Error(error?.response?.data?.message || "Failed to sync purchase");
        }
    }

    // ===== CS PORTAL / SERVICE DESK =====

    async listCSTickets(page = 1, limit = 20, filter = '', status = '') {
        try {
            const params = { page, limit };
            if (filter) params.filter = filter;
            if (status) params.status = status;
            const response = await axios.get(`${this.baseurl}/admin/cs-portal/tickets`, { params });
            return {
                tickets: response.data?.tickets || [],
                total: response.data?.total || 0
            };
        } catch (error) {
            console.error("Failed to list CS tickets:", error);
            return { tickets: [], total: 0 };
        }
    }

    async submitCSTicket(data) {
        try {
            const response = await axios.post(`${this.baseurl}/admin/cs-portal/tickets`, data);
            return response.data;
        } catch (error) {
            console.error("Failed to submit CS ticket:", error);
            throw new Error(error?.response?.data?.message || "Failed to submit ticket");
        }
    }

    async getCSTicket(id) {
        try {
            const response = await axios.get(`${this.baseurl}/admin/cs-portal/tickets/${id}`);
            return response.data;
        } catch (error) {
            console.error("Failed to get CS ticket:", error);
            throw new Error(error?.response?.data?.message || "Failed to get ticket");
        }
    }

    async syncCSTickets() {
        try {
            const response = await axios.post(`${this.baseurl}/admin/cs-portal/sync`);
            return response.data;
        } catch (error) {
            console.error("Failed to sync CS tickets:", error);
            throw new Error(error?.response?.data?.message || "Failed to sync tickets");
        }
    }

    async syncSingleCSTicket(ticketId) {
        try {
            const response = await axios.post(`${this.baseurl}/admin/cs-portal/sync-ticket`, { ticket_id: ticketId });
            return response.data;
        } catch (error) {
            console.error("Failed to sync CS ticket:", error);
            throw new Error(error?.response?.data?.message || "Failed to sync ticket");
        }
    }

    async removeCSTicket(ticketId) {
        try {
            const response = await axios.post(`${this.baseurl}/admin/cs-portal/remove-ticket`, { ticket_id: ticketId });
            return response.data;
        } catch (error) {
            console.error("Failed to remove CS ticket:", error);
            throw new Error(error?.response?.data?.message || "Failed to remove ticket");
        }
    }

    async addCSTicketComment(ticketId, body) {
        try {
            const response = await axios.post(`${this.baseurl}/admin/cs-portal/ticket-comments`, { ticket_id: ticketId, body });
            return response.data;
        } catch (error) {
            console.error("Failed to add CS ticket comment:", error);
            throw new Error(error?.response?.data?.message || "Failed to post comment");
        }
    }

    // ===== DEPLOY DASHBOARD =====

    async getDeployDashboardToken() {
        try {
            const response = await axios.get(`${this.baseurl}/admin/deploy-dashboard/token`);
            return response.data?.token || "";
        } catch (error) {
            console.error("Failed to get deploy dashboard token:", error);
            throw new Error(error?.response?.data?.message || "Failed to get deploy dashboard token");
        }
    }

    async updateDeployDashboardToken(token) {
        try {
            const response = await axios.put(`${this.baseurl}/admin/deploy-dashboard/token`, { token });
            return response.data;
        } catch (error) {
            console.error("Failed to update deploy dashboard token:", error);
            throw new Error(error?.response?.data?.message || "Failed to update deploy dashboard token");
        }
    }

    // ===== BULK USER OPERATIONS =====

    async exportUserListCSV(searchParams = {}) {
        try {
            const params = {};
            if (searchParams.brand_user_ids) {
                params.brand_user_ids = searchParams.brand_user_ids;
            }
            if (searchParams.verification_status) {
                params.verification_status = searchParams.verification_status;
            }
            if (searchParams.user_flag) {
                params.user_flag = searchParams.user_flag;
            }

            const response = await axios.get(`${this.baseurl}/admin/user/export`, {
                params,
                responseType: "blob"
            });
            return response.data;
        } catch (error) {
            console.error("Failed to export user list CSV:", error);
            let message = "Failed to export user list CSV";
            const data = error?.response?.data;
            if (data instanceof Blob) {
                try {
                    const parsed = JSON.parse(await data.text());
                    if (parsed?.message) message = parsed.message;
                } catch { /* fall back to default */ }
            } else if (data?.message) {
                message = data.message;
            }
            throw new Error(message);
        }
    }

    async bulkAdjustAccountCredits({ brandUserIds, currencyMode, unconvertedAmount, convertedAmount, reason }) {
        try {
            const response = await axios.post(`${this.baseurl}/admin/users/bulk/credits`, {
                brand_user_ids: brandUserIds,
                currency_mode: currencyMode,
                unconverted_amount: unconvertedAmount,
                converted_amount: convertedAmount,
                reason: reason
            });
            return response.data;
        } catch (error) {
            console.error("Failed to bulk adjust account credits:", error);
            throw new Error(error?.response?.data?.message || "Failed to bulk adjust account credits");
        }
    }

    async bulkGrantRMGCredits({ brandUserIds, credits, cashAmount, templateId, fundType, reason }) {
        try {
            const payload = {
                brand_user_ids: brandUserIds,
                reason
            };
            if (templateId) {
                payload.template_id = templateId;
            } else {
                payload.credits = credits;
                payload.cash_amount = cashAmount || '0';
            }
            if (fundType) {
                payload.fund_type = fundType;
            }
            const response = await axios.post(`${this.baseurl}/admin/users/bulk/rmg-credit`, payload);
            return response.data;
        } catch (error) {
            console.error("Failed to bulk grant RMG credits:", error);
            throw new Error(error?.response?.data?.message || "Failed to bulk grant RMG credits");
        }
    }

    async bulkRetractRMGCredits({ brandUserIds, credits, cashAmount, reason }) {
        try {
            const response = await axios.delete(`${this.baseurl}/admin/users/bulk/rmg-credit`, {
                data: {
                    brand_user_ids: brandUserIds,
                    credits,
                    cash_amount: cashAmount || '0',
                    reason
                }
            });
            return response.data;
        } catch (error) {
            console.error("Failed to bulk retract RMG credits:", error);
            throw new Error(error?.response?.data?.message || "Failed to bulk retract RMG credits");
        }
    }

    // ===== Global Exclusions =====

    async uploadExclusionCSV(formData) {
        const response = await axios.post(`${this.baseurl}/admin/global-exclusions/upload`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
        });
        return response.data;
    }

    async listExclusionUploads(limit = 50, offset = 0) {
        const response = await axios.get(`${this.baseurl}/admin/global-exclusions/uploads`, {
            params: { limit, offset }
        });
        return response.data;
    }

    async listLocksForReview(limit = 50, offset = 0) {
        const response = await axios.get(`${this.baseurl}/admin/global-exclusions/review`, {
            params: { limit, offset }
        });
        return response.data;
    }

    async reviewExclusionLock(lockId, decision, adminNote = '') {
        const response = await axios.post(`${this.baseurl}/admin/global-exclusions/review`, {
            lock_id: lockId,
            decision,
            admin_note: adminNote
        });
        return response.data;
    }

    async deactivateExclusionRecord(id, reason = '') {
        const response = await axios.delete(`${this.baseurl}/admin/global-exclusions/${id}`, {
            data: { reason }
        });
        return response.data;
    }

    // ============================================================================
    // Segmentation
    // ============================================================================

    async listSegments(status) {
        const params = {};
        if (status) params.status = status;
        const response = await axios.get(`${this.baseurl}/admin/segments`, { params });
        return response.data.segments || [];
    }

    async createSegment({ name, description }) {
        const response = await axios.post(`${this.baseurl}/admin/segments`, { name, description });
        return response.data.segment;
    }

    async getSegment(segmentId) {
        const response = await axios.get(`${this.baseurl}/admin/segments/${segmentId}`);
        return response.data.segment;
    }

    async updateSegment(segmentId, { name, description }) {
        const response = await axios.patch(`${this.baseurl}/admin/segments/${segmentId}`, { name, description });
        return response.data;
    }

    async transitionSegmentStatus(segmentId, status) {
        const response = await axios.post(`${this.baseurl}/admin/segments/${segmentId}/status`, { status });
        return response.data;
    }

    async listSegmentMembers(segmentId, { limit = 50, offset = 0 } = {}) {
        const response = await axios.get(`${this.baseurl}/admin/segments/${segmentId}/members`, {
            params: { limit, offset }
        });
        return response.data;
    }

    async removeSegmentMember(segmentId, brandUserId) {
        const response = await axios.delete(`${this.baseurl}/admin/segments/${segmentId}/members/${brandUserId}`);
        return response.data;
    }

    async addSegmentMember(segmentId, brandUserId) {
        const response = await axios.post(`${this.baseurl}/admin/segments/${segmentId}/members`, {
            brand_user_id: brandUserId
        });
        return response.data;
    }

    async listSegmentProducts(segmentId) {
        const response = await axios.get(`${this.baseurl}/admin/segments/${segmentId}/products`);
        return response.data.products || [];
    }

    async linkSegmentProduct(segmentId, productId) {
        const response = await axios.post(`${this.baseurl}/admin/segments/${segmentId}/products`, { product_id: productId });
        return response.data;
    }

    async unlinkSegmentProduct(segmentId, productId) {
        const response = await axios.delete(`${this.baseurl}/admin/segments/${segmentId}/products/${productId}`);
        return response.data;
    }

    async presignSegmentUpload(segmentId, fileName) {
        const response = await axios.post(`${this.baseurl}/admin/segments/${segmentId}/upload/presign`, { fileName });
        return response.data;
    }

    async confirmSegmentUpload(segmentId, s3Key) {
        const response = await axios.post(`${this.baseurl}/admin/segments/${segmentId}/upload/confirm`, { s3_key: s3Key });
        return response.data.task;
    }

    async listSegmentUploadTasks(segmentId, limit = 20) {
        const response = await axios.get(`${this.baseurl}/admin/segments/${segmentId}/upload-tasks`, {
            params: { limit }
        });
        return response.data.tasks || [];
    }

    async getUploadTask(taskId) {
        const response = await axios.get(`${this.baseurl}/admin/upload-tasks/${taskId}`);
        return response.data.task;
    }

    async listSegmentsForProduct(productId) {
        const response = await axios.get(`${this.baseurl}/admin/purchase-offers/${productId}/segments`);
        return response.data.segments || [];
    }

    async setProductSegmentGating(productId, isSegmentGated) {
        const response = await axios.patch(`${this.baseurl}/admin/purchase-offers/${productId}/segment-gating`, {
            is_segment_gated: isSegmentGated
        });
        return response.data;
    }

    async listSegmentPromotions(segmentId) {
        const response = await axios.get(`${this.baseurl}/admin/segments/${segmentId}/promotions`);
        return response.data.promotions || [];
    }

    // Number() the id: callers pass route.params.id, which is a string, and the
    // Go handler decodes promotion_id into an int64 — a quoted number fails the
    // decode and surfaces as "promotion_id is required".
    async linkSegmentPromotion(segmentId, promotionId) {
        const response = await axios.post(`${this.baseurl}/admin/segments/${segmentId}/promotions`, { promotion_id: Number(promotionId) });
        return response.data;
    }

    async unlinkSegmentPromotion(segmentId, promotionId) {
        const response = await axios.delete(`${this.baseurl}/admin/segments/${segmentId}/promotions/${promotionId}`);
        return response.data;
    }

    async listSegmentsForPromotion(promotionId) {
        const response = await axios.get(`${this.baseurl}/admin/promotions/${promotionId}/segments`);
        return response.data.segments || [];
    }

    // Uploads a File to the presigned URL the backend returned.
    // Note: per backend convention, we deliberately do NOT set Content-Type
    // so the signature stays valid regardless of what the browser sends.
    async uploadSegmentCsvToPresignedURL(presignedURL, file) {
        // Use a clean axios instance so the global auth interceptor does not
        // inject the Okta JWT into the S3 request. Presigned URLs carry their
        // own auth via query params — an extra, unsigned Authorization header
        // makes S3 reject the PUT (surfaces in the browser as a CORS error).
        const s3Axios = axios.create();
        await s3Axios.put(presignedURL, file, {
            headers: { "Content-Type": "" }
        });
    }
}
